import { WbsItemStatus } from '../../../domain/entity/WbsItemEntity'
import { dateTimeV2Service } from '../../../domain/value-object/DateTimeV2'
import {
  applyAuditFieldsOnUpdate,
  dayOffsetToDate,
  TODAY_DAY_OFFSET as DEMO_TODAY_DAY_OFFSET,
} from '../shared'
import {
  DEMO_ACCOUNTABLE_USER_UUIDS,
  DEMO_PM_USER_UUID,
  DEMO_TEAM_ROSTERS,
  demoProjectMember,
  demoProjectMemberRefByUserUuid,
  requireDemoProjectMemberRef,
  demoTeams,
  demoTeamByUserUuid,
} from './members'
import { demoSprintsByUuid } from './sprints'
import { demoCommentSummary } from './comments'
import { demoUser } from '../tenants/tenant'
import { demoProject } from './project'
import { ProjectPlanCumulation } from '../../../lib/functions/projectPlan'
import { CustomEnumCombinationDirection } from '../../../lib/commons/appFunction'
import iconBug from '../../../assets/icons/wbs-item-type/bug.svg'
import iconChangeRequest from '../../../assets/icons/wbs-item-type/change_request.svg'
import iconDeliverable from '../../../assets/icons/wbs-item-type/deliverable.svg'
import iconDeliverableList from '../../../assets/icons/wbs-item-type/deliverable_list.svg'
import iconIssue from '../../../assets/icons/wbs-item-type/issue.svg'
import iconMeeting from '../../../assets/icons/wbs-item-type/meeting.svg'
import iconProcess from '../../../assets/icons/wbs-item-type/process.svg'
import iconRisk from '../../../assets/icons/wbs-item-type/risk.svg'
import iconTask from '../../../assets/icons/wbs-item-type/task.svg'
import iconWorkgroup from '../../../assets/icons/wbs-item-type/workgroup.svg'

// StatusComboBox（WBSステータスセルのドロップダウン）は各選択肢の`value`
// しか読まず、表示ラベルはgetWbsItemStatusLabelから取得するため、
// name/nameI18nは未使用のダミー値でよい。
// デモデータが扱うWBSステータス。本体のWbsItemStatus enumと同じ値を、
// ビルダーへstringリテラルで渡せるunion型として持つ。
export type DemoWbsItemStatus = `${WbsItemStatus}`

export const DEMO_WBS_STATUS_VALUES = Object.values(WbsItemStatus).map(
  value => ({
    customEnumCode: 'WBS_STATUS',
    value,
    name: value,
    nameI18n: [`ja:${value}`, `en:${value}`],
    combinations: [],
  })
)

// CustomEnumCellRenderer/Editorは`name`/`nameI18n`を直接表示に使う
// （独自のラベル参照を持つWBSステータスセルとは異なる）ため、
// 上記のようなダミー値ではなく実際の表示ラベルが必要。
const buildDemoCustomEnumValues = (
  customEnumCode: string,
  entries: { value: string; ja: string; en: string }[]
) =>
  entries.map(({ value, ja, en }) => ({
    customEnumCode,
    value,
    name: ja,
    nameI18n: [`ja:${ja}`, `en:${en}`],
    combinations: [],
  }))

export const DEMO_WBS_PRIORITY_VALUES = buildDemoCustomEnumValues(
  'WBS_PRIORITY',
  [
    { value: 'CRITICAL', ja: 'クリティカル', en: 'Critical' },
    { value: 'HIGH', ja: '高', en: 'High' },
    { value: 'MIDDLE', ja: '中', en: 'Middle' },
    { value: 'LOW', ja: '低', en: 'Low' },
  ]
)

export const DEMO_WBS_DIFFICULTY_VALUES = buildDemoCustomEnumValues(
  'WBS_DIFFICULTY',
  [
    { value: 'S_PLUS_PLUS', ja: 'S++', en: 'S++' },
    { value: 'S_PLUS', ja: 'S+', en: 'S+' },
    { value: 'S', ja: 'S', en: 'S' },
    { value: 'A', ja: 'A', en: 'A' },
    { value: 'B', ja: 'B', en: 'B' },
    { value: 'C', ja: 'C', en: 'C' },
    { value: 'D', ja: 'D', en: 'D' },
    { value: 'E', ja: 'E', en: 'E' },
  ]
)

// サブステータスと、連動して設定されるWBSステータスの対応。
const DEMO_SUBSTATUS_ENTRIES: {
  value: string
  ja: string
  en: string
  status: WbsItemStatus
}[] = [
  {
    value: 'NOT_STARTED',
    ja: '未着手',
    en: 'Not started',
    status: WbsItemStatus.TODO,
  },
  {
    value: 'IN_PROGRESS',
    ja: '対応中',
    en: 'In progress',
    status: WbsItemStatus.DOING,
  },
  {
    value: 'CODE_REVIEW',
    ja: 'コードレビュー中',
    en: 'Code review',
    status: WbsItemStatus.REVIEW,
  },
  {
    value: 'PO_REVIEW',
    ja: 'POレビュー中',
    en: 'PO review',
    status: WbsItemStatus.REVIEW,
  },
  {
    value: 'COMPLETED',
    ja: '完了',
    en: 'Completed',
    status: WbsItemStatus.DONE,
  },
  {
    value: 'DISCARDED',
    ja: '破棄',
    en: 'Discarded',
    status: WbsItemStatus.DISCARD,
  },
]

// BIDIRECTIONのcombinationを付けると、本体のcustomEnumValueSetter
// （ProjectPlanNew/gridOptions/defaultColumns.ts）がサブステータス選択時に
// combinedValues[0]のステータスを自動設定する。
export const DEMO_WBS_SUBSTATUS_VALUES = DEMO_SUBSTATUS_ENTRIES.map(
  ({ value, ja, en, status }) => ({
    customEnumCode: 'WBS_SUBSTATUS',
    value,
    name: ja,
    nameI18n: [`ja:${ja}`, `en:${en}`],
    combinations: [
      {
        direction: CustomEnumCombinationDirection.BIDIRECTION,
        combinedEnumCode: 'WBS_STATUS',
        combinedValues: [
          {
            customEnumCode: 'WBS_STATUS',
            value: status,
            name: status,
            nameI18n: [`ja:${status}`, `en:${status}`],
          },
        ],
      },
    ],
  })
)

// 単票用。custom_enums/valuesとは形が異なり、i18nLabelとstatusを持つ
// （wbsSubStatusRepositoryAdaptor）。
export const DEMO_WBS_SUBSTATUS_TYPE_VALUES = DEMO_SUBSTATUS_ENTRIES.map(
  ({ value, ja, en, status }) => ({
    value,
    i18nLabel: [`ja:${ja}`, `en:${en}`],
    status,
  })
)

// 各WBS項目のサブステータス初期値。マスタ（DEMO_SUBSTATUS_ENTRIES）は
// サブステータスからWBSステータスへの対応を持つので、それを逆に引いて
// statusに対応する既定のサブステータスを決める。REVIEWのように複数の
// サブステータスが同じstatusを指す場合は、マスタで先に定義されている方
// （CODE_REVIEW）を既定とする。
const DEFAULT_SUBSTATUS_BY_STATUS: { [status: string]: string } =
  DEMO_SUBSTATUS_ENTRIES.reduce<{ [status: string]: string }>(
    (acc, { value, status }) => {
      acc[status] ??= value
      return acc
    },
    {}
  )

// グリッドの行追加・移動ロジックがrootTypeで分岐するため、WBS項目タイプは
// 階層（WORKGROUP > PROCESS > DELIVERABLE > TASK）を全て揃える必要がある。
export const demoWbsItemTypeWorkgroup = {
  uuid: 'demo-wbs-item-type-workgroup',
  code: 'WORKGROUP',
  name: 'ワークグループ',
  rootType: 'WORKGROUP',
  iconUrl: iconWorkgroup,
}

// デモのWBSツリーのルート行（ワークグループ）専用のtypeDto。
// 単票（view/pages/WbsItem/index.tsx）のswitchはtypeDto.rootTypeで描画する
// コンポーネントを選ぶが、WORKGROUPのcaseが存在せず`return <></>`に落ちるため、
// rootTypeにWORKGROUPを入れるとアイコン押下時にダイアログが白画面になる。
// アイコン(iconUrl)・表示名(name)はrootTypeと独立して扱われる
// （WbsItemIcon は type.iconUrl を描画するだけ）ので、rootTypeだけをPROCESSに
// 倒せば「見た目はワークグループ、単票は工程」を本体無改修で両立できる。
// タイプ一覧(demoWbsItemTypes)に載せるWORKGROUPは
// BaseWbsItemType.isEmpty()の成立に必要なため別途そのまま残す。
export const demoWbsItemTypeWorkgroupRoot = {
  uuid: 'demo-wbs-item-type-workgroup-root',
  code: 'PROCESS',
  name: 'ワークグループ',
  rootType: 'PROCESS',
  iconUrl: iconWorkgroup,
}
export const demoWbsItemTypeProcess = {
  uuid: 'demo-wbs-item-type-process',
  code: 'PROCESS',
  name: '工程',
  rootType: 'PROCESS',
  iconUrl: iconProcess,
}
export const demoWbsItemTypeDeliverableList = {
  uuid: 'demo-wbs-item-type-deliverable-list',
  code: 'DELIVERABLE_LIST',
  name: '成果物一覧',
  rootType: 'DELIVERABLE_LIST',
  iconUrl: iconDeliverableList,
}
export const demoWbsItemTypeDeliverable = {
  uuid: 'demo-wbs-item-type-deliverable',
  code: 'DELIVERABLE',
  name: '成果物',
  rootType: 'DELIVERABLE',
  iconUrl: iconDeliverable,
}

export const demoWbsItemType = {
  uuid: 'demo-wbs-item-type-task',
  code: 'TASK',
  name: 'タスク',
  rootType: 'TASK',
  iconUrl: iconTask,
}
// BaseWbsItemType（src/store/project.ts）は5つのrootType全て
// （WORKGROUP/PROCESS/DELIVERABLE_LIST/DELIVERABLE/TASK）が揃っていないと
// isEmpty()がtrueのままになり、行追加のショートカットやコンテキストメニューが動かない。
export const demoWbsItemTypes = [
  demoWbsItemTypeWorkgroup,
  demoWbsItemTypeProcess,
  demoWbsItemTypeDeliverableList,
  demoWbsItemTypeDeliverable,
  demoWbsItemType,
]

// チケット型はWbsItemTypeVO.isTicket()が`!!baseUuid`で判定するため、
// 通常のDELIVERABLE/TASK（demoWbsItemTypeDeliverable/demoWbsItemType）とは
// 別オブジェクトとしてbaseUuidを持たせる必要がある。createNewProjectPlanNewRow
// （ProjectPlanNew/projectPlanNew.ts）はticketTypeを`code.replace('_LIST', '')`
// で導出するため、チケットリスト型のcodeは対応するチケット型のcodeに
// `_LIST`サフィックスを付けたものにする必要がある。
// 実アプリのチケットタイプは課題管理(ISSUE)・不具合管理(BUG)・
// リスク管理(RISK)・変更管理(CHANGE_REQUEST)・会議管理(MEETING)の5種類
// （src/view/pages/router/index.tsxのリダイレクト定義に準拠）。
// アイコンは種別ごとに異なる（課題/不具合/リスク/変更/会議）。本番は
// バックエンドが投入したプリセットのiconUrlを返すが、デモはAPIをモックするため
// 同じ実CDN画像(/cdn/global/icons/{code}.svg)をローカルにバンドルして渡す。
// チケットリスト型（親）はWbsItemIconがticket_list.svgを重ねて描画するため、
// 子と同じ種別アイコンを渡して「種別＋一覧」の見た目にする。
const buildTicketType = (overrides: {
  code: string
  name: string
  icon: string
}) => ({
  uuid: `demo-ticket-type-${overrides.code.toLowerCase()}`,
  code: overrides.code,
  name: overrides.name,
  rootType: 'TASK',
  iconUrl: overrides.icon,
  baseUuid: demoWbsItemType.uuid,
})

const buildTicketTypeList = (overrides: {
  code: string
  name: string
  icon: string
  child: ReturnType<typeof buildTicketType>
}) => ({
  uuid: `demo-ticket-type-${overrides.code.toLowerCase()}`,
  code: overrides.code,
  name: overrides.name,
  rootType: 'DELIVERABLE',
  iconUrl: overrides.icon,
  baseUuid: demoWbsItemTypeDeliverable.uuid,
  child: overrides.child,
})

const demoTicketTypeIssue = buildTicketType({
  code: 'ISSUE',
  name: '課題',
  icon: iconIssue,
})
export const demoTicketTypeIssueList = buildTicketTypeList({
  code: 'ISSUE_LIST',
  name: '課題管理',
  icon: iconIssue,
  child: demoTicketTypeIssue,
})
const demoTicketTypeBug = buildTicketType({
  code: 'BUG',
  name: '不具合',
  icon: iconBug,
})
export const demoTicketTypeBugList = buildTicketTypeList({
  code: 'BUG_LIST',
  name: '不具合管理',
  icon: iconBug,
  child: demoTicketTypeBug,
})
const demoTicketTypeRisk = buildTicketType({
  code: 'RISK',
  name: 'リスク',
  icon: iconRisk,
})
export const demoTicketTypeRiskList = buildTicketTypeList({
  code: 'RISK_LIST',
  name: 'リスク管理',
  icon: iconRisk,
  child: demoTicketTypeRisk,
})
const demoTicketTypeChangeRequest = buildTicketType({
  code: 'CHANGE_REQUEST',
  name: '変更',
  icon: iconChangeRequest,
})
export const demoTicketTypeChangeRequestList = buildTicketTypeList({
  code: 'CHANGE_REQUEST_LIST',
  name: '変更管理',
  icon: iconChangeRequest,
  child: demoTicketTypeChangeRequest,
})
const demoTicketTypeMeeting = buildTicketType({
  code: 'MEETING',
  name: '会議',
  icon: iconMeeting,
})
export const demoTicketTypeMeetingList = buildTicketTypeList({
  code: 'MEETING_LIST',
  name: '会議管理',
  icon: iconMeeting,
  child: demoTicketTypeMeeting,
})
// GET /api/v1/projects/wbs_item_types/tickets のモック応答。store/project.ts
// が`.filter(v => !!v.child)`（チケットリスト型）と`.filter(v => !v.child)`
// （チケット型）に振り分けるため、両方をフラットに含める。
export const demoTicketTypes = [
  demoTicketTypeIssueList,
  demoTicketTypeIssue,
  demoTicketTypeBugList,
  demoTicketTypeBug,
  demoTicketTypeRiskList,
  demoTicketTypeRisk,
  demoTicketTypeChangeRequestList,
  demoTicketTypeChangeRequest,
  demoTicketTypeMeetingList,
  demoTicketTypeMeeting,
]

// 初期データの担当者フィールドは必ず実在のメンバーを指す（未設定はない）。
type DemoMemberRef = ReturnType<typeof requireDemoProjectMemberRef>

// 生成する全WBS項目body共通のベースフィールド。個別の上書きは
// buildWbsItemBody内でこの上にspreadする。
export const baseWbsItemBody = {
  projectUuid: demoProject.uuid,
  revision: '1',
  description: '',
  startIf: '',
  completeIf: '',
  rule: '',
  // team/accountable/responsible/assignee はここに持たせない。画面から
  // 新規追加した行のbodyもこのベースのspreadで組み立てられる（handlers.tsの
  // plans/delta/batch等）ため、ここに値を置くと未入力の行にチーム・担当者が
  // 埋まった状態で作られてしまう。初期データは呼び出し元が明示的に値を渡す。
  //
  // team未設定の行はスプリント列のoptionsFilter
  // （option.teamUuid === wbsItem.team?.uuid）を満たせず候補が0件になるが、
  // チーム未設定ではスプリントを選べないのは本番と同じ挙動。
  critical: false,
  deadline: null,
  difficulty: '',
  estimatedStoryPoint: 0,
  priority: '',
  createdAt: dateTimeV2Service.now(),
  createdBy: demoUser,
  updatedAt: dateTimeV2Service.now(),
  updatedBy: demoUser,
  watchers: [],
  // ProjectPlanRowBody's constructor (projectPlanNew.ts) reads
  // w.scheduledDate.startDate/w.actualDate.startDate unconditionally, with no
  // optional chaining. The ticket single-sheet's toCreateInput sends these as
  // `undefined` (not an empty object) when the user leaves them blank, unlike
  // Grid-added rows which always start from an explicit
  // { startDate: undefined, endDate: undefined }. Without this default here,
  // saving a ticket via the single-sheet with no scheduled/actual date throws
  // inside refreshRow's response mapping, aborting it silently and leaving
  // the just-created row missing from the grid.
  scheduledDate: { startDate: undefined, endDate: undefined },
  actualDate: { startDate: undefined, endDate: undefined },
}

export const demoWbsItemBodies: { [uuid: string]: any } = {}

// 投下工数の明細（誰が・いつ・何時間）。TaskActualWorkList（投下工数セルの
// popper）は日付別の行を描画するため、合計値であるwbsItemBodyのactualHour
// からは復元できない。明細をこちらを単一の真実として保持し、actualHourは
// syncActualHourで常にこの合計から導出する。キーはtask uuid。
export const demoTaskActualWorksByTaskUuid: { [taskUuid: string]: any[] } = {}

// 明細の合計をwbsItemBodyのactualHour/actualAmountへ反映する。明細を
// 変更した箇所は必ずこれを呼び、グリッドの投下工数列とpopperの合計が
// ずれないようにする。
export const syncActualHour = (taskUuid: string) => {
  const body = demoWbsItemBodies[taskUuid]
  if (!body) return
  const total = (demoTaskActualWorksByTaskUuid[taskUuid] ?? []).reduce(
    (sum, actualWork) => sum + actualWork.hour,
    0
  )
  body.actualHour = total
  body.actualAmount = total
}

// 同一(task, member, actualDate)の明細は1件に集約するのが実仕様のため、
// 既存レコードがあればhourを上書きし、無ければ追加するupsertにする。
// 明細変更後のactualHour再計算まで行う。
export const upsertDemoTaskActualWork = (params: {
  taskUuid: string
  projectMember: any
  actualDate: string
  hour: number
}) => {
  const { taskUuid, projectMember, actualDate, hour } = params
  const actualWorks = (demoTaskActualWorksByTaskUuid[taskUuid] ??= [])
  const existing = actualWorks.find(
    actualWork =>
      actualWork.projectMember.uuid === projectMember.uuid &&
      actualWork.actualDate === actualDate
  )
  if (existing) {
    existing.hour = hour
  } else {
    actualWorks.push({
      uuid: `demo-actual-work-${taskUuid}-${projectMember.uuid}-${actualDate}`,
      lockVersion: 1,
      projectMember,
      actualDate,
      hour,
    })
  }
  syncActualHour(taskUuid)
}
export const demoWbsItemSkeletonNodesByUuid: { [uuid: string]: any } = {}

// toCreateInput（WbsItem/model）はscheduledDate/actualDateを常に
// `{ startDate: undefined, endDate: undefined }`ではなく`undefined`そのもの
// として送ってくることがあり、`{...baseWbsItemBody, ...wbsItemOverrides}`の
// spreadだと明示的なundefinedがbaseWbsItemBodyのデフォルトを上書きしてしまう。
// ProjectPlanRowBodyがw.scheduledDate.startDateをoptional chainingなしで
// 読むため、チケット系のcreate/delta/batchハンドラは必ずこれでフォールバック
// させてからbodyへ書き込む。
export const withWbsItemDateDefaults = <T extends Record<string, any>>(
  wbsItemOverrides: T
): T => ({
  ...wbsItemOverrides,
  scheduledDate: wbsItemOverrides.scheduledDate ?? {
    startDate: undefined,
    endDate: undefined,
  },
  actualDate: wbsItemOverrides.actualDate ?? {
    startDate: undefined,
    endDate: undefined,
  },
})

const buildWbsItemBody = (overrides: {
  uuid: string
  code: string
  type: string
  typeDto: typeof demoWbsItemTypeWorkgroup
  displayName: string
  status: DemoWbsItemStatus
  startDate: string
  endDate: string
  estimatedHour: number
  actualHour: number
  team?: (typeof demoTeams)[number]
  accountable?: DemoMemberRef
  responsible?: DemoMemberRef
  assignee?: DemoMemberRef
}) => {
  const {
    startDate,
    endDate,
    estimatedHour,
    actualHour,
    team,
    accountable,
    responsible,
    assignee,
    ...rest
  } = overrides
  const isDone = overrides.status === 'DONE'
  demoWbsItemBodies[overrides.uuid] = {
    ...baseWbsItemBody,
    ...rest,
    // 体制はbaseWbsItemBodyに持たせていない（理由はそちらのコメント参照）
    // ため、呼び出し元が渡した値をそのまま使う。
    team,
    accountable,
    responsible,
    assignee,
    // ProjectPlanRowBodyはtypeDto/baseTypeDtoが無いと
    // store.getState().project.wbsItemTypesにフォールバックする。
    // 行追加直後などその参照が必ず成功するとは限らないため、両方を直接与えておく。
    typeDto: overrides.typeDto,
    baseTypeDto: overrides.typeDto,
    lockVersion: 1,
    substatus: DEFAULT_SUBSTATUS_BY_STATUS[overrides.status] ?? '',
    path: '',
    estimatedHour,
    actualHour,
    estimatedAmount: estimatedHour,
    actualAmount: actualHour,
    scheduledDate: {
      startDate,
      endDate,
    },
    actualDate: {
      startDate: overrides.status === 'TODO' ? null : startDate,
      endDate: isDone ? endDate : null,
    },
  }
}

// TASKの投下工数を稼働日ごとの明細レコードへ分割する。実データは1人が
// 1日8時間を上限に複数日かけて作業した形になるため、開始日から土日を
// 飛ばして1日最大8時間ずつ割り当て、端数は最終日に寄せる。
// 明細が2人以上に分かれている方がpopperのメンバー別Total行の確認になるので、
// 8時間を超える（＝複数日にまたがる）タスクは担当者と副担当の2名に配分する。
const HOURS_PER_WORKDAY = 8
const MAX_ACTUAL_WORK_DAYS = 10

const isWeekend = (date: Date) => {
  const weekday = date.getUTCDay()
  return weekday === 0 || weekday === 6
}

// startDate自体が土日のこともある（デモの予定日は稼働日に揃っていない）ため、
// まず起点を次の平日まで送ってから、そこを0日目として平日を数える。
const addWorkdays = (startDate: string, workdayOffset: number) => {
  const date = new Date(`${startDate}T00:00:00Z`)
  while (isWeekend(date)) {
    date.setUTCDate(date.getUTCDate() + 1)
  }
  let remaining = workdayOffset
  while (remaining > 0) {
    date.setUTCDate(date.getUTCDate() + 1)
    if (!isWeekend(date)) remaining--
  }
  return date.toISOString().slice(0, 10)
}

// 終端の日付から遡って稼働日を数える。未完了タスクの実績を今日以前に
// 収めるために使う。
const subtractWorkdays = (endDate: string, workdayOffset: number) => {
  const date = new Date(`${endDate}T00:00:00Z`)
  while (isWeekend(date)) {
    date.setUTCDate(date.getUTCDate() - 1)
  }
  let remaining = workdayOffset
  while (remaining > 0) {
    date.setUTCDate(date.getUTCDate() - 1)
    if (!isWeekend(date)) remaining--
  }
  return date.toISOString().slice(0, 10)
}

const buildTaskActualWorks = (overrides: {
  uuid: string
  status: DemoWbsItemStatus
  startDate: string
  actualHour: number
  assignee?: DemoMemberRef
  accountable?: DemoMemberRef
}) => {
  const { uuid, status, startDate, actualHour, assignee, accountable } =
    overrides
  // 着手前のタスク（未着手・着手前に破棄）には投下実績が無いのが自然。
  if (isNotStartedYet(status, startDate) || actualHour <= 0) return

  // 実績を積むのは担当者と責任者のみ。1日で終わるタスクは担当者だけ、
  // 複数日にまたがるタスクは2名で分担する。
  const members = [
    assignee ?? demoProjectMember,
    accountable ?? demoProjectMember,
  ]
  const dayCount = Math.min(
    Math.ceil(actualHour / HOURS_PER_WORKDAY),
    MAX_ACTUAL_WORK_DAYS
  )
  // dayCountで頭打ちにした場合でも合計がactualHourと一致するよう、
  // 1日あたりの時間は割り算で決めて端数だけ最終日に載せる。1日あたりの
  // 実績が小数になると明細に「8.8時間」のような値が並ぶため、整数時間に
  // 切り捨てて端数は最終日へ寄せる。
  const hourPerDay = Math.floor(actualHour / dayCount)
  let assigned = 0

  // 実績日は、完了・破棄は予定開始日から順に、未完了は今日から遡って
  // 積む。未完了タスクの実績が未来日にならないようにするため。
  const isClosed = status === 'DONE' || status === 'DISCARD'
  const actualDateAt = (dayIndex: number) =>
    isClosed
      ? addWorkdays(startDate, dayIndex)
      : subtractWorkdays(dayOffsetToDate(DEMO_TODAY_DAY_OFFSET), dayIndex)

  for (let dayIndex = 0; dayIndex < dayCount; dayIndex++) {
    const isLastDay = dayIndex === dayCount - 1
    const hour = isLastDay ? actualHour - assigned : hourPerDay
    assigned += hour
    // 1日で終わるタスクは担当者1人だけにして、複数日のものだけ2人に散らす。
    const member = members[dayCount === 1 ? 0 : dayIndex % 2] ?? members[0]
    upsertDemoTaskActualWork({
      taskUuid: uuid,
      projectMember: member,
      actualDate: actualDateAt(dayIndex),
      hour,
    })
  }
}

// スケルトンノードとそのbodyを1つずつ生成・登録し、呼び出し元が親の
// `children`にネストできるようスケルトンノードを返す。
const addWbsItem = (overrides: {
  uuid: string
  code: string
  type: string
  typeDto: typeof demoWbsItemTypeWorkgroup
  displayName: string
  status: DemoWbsItemStatus
  startDate: string
  endDate: string
  estimatedHour: number
  actualHour: number
  team?: (typeof demoTeams)[number]
  accountable?: DemoMemberRef
  responsible?: DemoMemberRef
  assignee?: DemoMemberRef
}) => {
  buildWbsItemBody(overrides)
  // 投下工数の明細はTASKにのみ持たせる。PROCESS/DELIVERABLEのactualHourは
  // 配下の集計値であって自分自身の実績ではないため、明細生成の対象外。
  if (overrides.type === 'TASK') {
    buildTaskActualWorks(overrides)
  }
  const node = {
    uuid: overrides.uuid,
    lockVersion: 1,
    type: overrides.type,
    wbsItemUuid: overrides.uuid,
    children: [] as any[],
  }
  demoWbsItemSkeletonNodesByUuid[overrides.uuid] = node
  return node
}

export const REQUIREMENTS_PHASE_ITEMS: {
  code: string
  type: 'PROCESS' | 'DELIVERABLE_LIST' | 'DELIVERABLE' | 'TASK'
  name: string
  // 担当者。成果物（DELIVERABLE）にのみ持たせる。
  // 成果物一覧・タスクは直近の成果物から引き継ぎ、工程は責任者が担当する
  // （membersByCode）ため、ここで指定するのは成果物だけでよい。
  assigneeUserUuid?: string
  startDay: number
  endDay: number
  estimatedHour: number
  depth: number
}[] = [
  {
    code: 'GWIS7KG6',
    type: 'PROCESS',
    name: '要件定義',
    startDay: 0,
    endDay: 84,
    estimatedHour: 1144,
    depth: 0,
  },
  {
    code: 'YRQL3TCX',
    type: 'DELIVERABLE_LIST',
    name: '要件定義セッション',
    startDay: 5,
    endDay: 21,
    estimatedHour: 120,
    depth: 1,
  },
  {
    code: 'FLEQ1VSA',
    type: 'DELIVERABLE',
    name: '第1回',
    assigneeUserUuid: 'demo-user-2',
    startDay: 5,
    endDay: 8,
    estimatedHour: 56,
    depth: 2,
  },
  {
    code: 'TMKIUCRK',
    type: 'TASK',
    name: '会議準備',
    startDay: 5,
    endDay: 5,
    estimatedHour: 8,
    depth: 3,
  },
  {
    code: 'ACOIFRJN',
    type: 'TASK',
    name: 'セッション実施',
    startDay: 5,
    endDay: 6,
    estimatedHour: 32,
    depth: 3,
  },
  {
    code: 'P0ZRA175',
    type: 'TASK',
    name: '議事録送付',
    startDay: 7,
    endDay: 8,
    estimatedHour: 16,
    depth: 3,
  },
  {
    code: 'FDABJCEO',
    type: 'DELIVERABLE',
    name: '第2回',
    assigneeUserUuid: 'demo-user-3',
    startDay: 11,
    endDay: 14,
    estimatedHour: 32,
    depth: 2,
  },
  {
    code: 'TKQ1FYM8',
    type: 'TASK',
    name: '初版作成',
    startDay: 11,
    endDay: 11,
    estimatedHour: 8,
    depth: 3,
  },
  {
    code: 'PU4STGWT',
    type: 'TASK',
    name: '内部レビュー',
    startDay: 12,
    endDay: 13,
    estimatedHour: 8,
    depth: 3,
  },
  {
    code: 'NEJX8LDM',
    type: 'TASK',
    name: 'レビュー結果反映',
    startDay: 14,
    endDay: 14,
    estimatedHour: 16,
    depth: 3,
  },
  {
    code: 'RK38CFKL',
    type: 'DELIVERABLE',
    name: '第3回',
    assigneeUserUuid: demoUser.uuid,
    startDay: 17,
    endDay: 21,
    estimatedHour: 32,
    depth: 2,
  },
  {
    code: 'IP5R1EMC',
    type: 'TASK',
    name: '初版作成',
    startDay: 17,
    endDay: 17,
    estimatedHour: 8,
    depth: 3,
  },
  {
    code: '6UD281A8',
    type: 'TASK',
    name: '内部レビュー',
    startDay: 18,
    endDay: 19,
    estimatedHour: 8,
    depth: 3,
  },
  {
    code: 'B75UBNFM',
    type: 'TASK',
    name: 'レビュー結果反映',
    startDay: 20,
    endDay: 21,
    estimatedHour: 16,
    depth: 3,
  },
  {
    code: 'B4XNZ2HM',
    type: 'DELIVERABLE_LIST',
    name: '業務フロー',
    startDay: 18,
    endDay: 52,
    estimatedHour: 128,
    depth: 1,
  },
  {
    code: 'OQJRZWE1',
    type: 'DELIVERABLE',
    name: 'EC販売管理領域',
    assigneeUserUuid: 'demo-user-5',
    startDay: 18,
    endDay: 33,
    estimatedHour: 24,
    depth: 2,
  },
  {
    code: 'IWA4HKYM',
    type: 'TASK',
    name: '初版作成',
    startDay: 18,
    endDay: 24,
    estimatedHour: 8,
    depth: 3,
  },
  {
    code: '7LD93FNC',
    type: 'TASK',
    name: '内部レビュー',
    startDay: 25,
    endDay: 28,
    estimatedHour: 8,
    depth: 3,
  },
  {
    code: 'M2RYVPRP',
    type: 'TASK',
    name: 'レビュー結果反映',
    startDay: 29,
    endDay: 33,
    estimatedHour: 8,
    depth: 3,
  },
  {
    code: '8GTW2IV9',
    type: 'DELIVERABLE',
    name: 'EC在庫管理領域',
    assigneeUserUuid: 'demo-user-2',
    startDay: 33,
    endDay: 43,
    estimatedHour: 48,
    depth: 2,
  },
  {
    code: 'E6P5R3EP',
    type: 'TASK',
    name: '初版作成',
    startDay: 33,
    endDay: 35,
    estimatedHour: 24,
    depth: 3,
  },
  {
    code: 'H6I6NKAW',
    type: 'TASK',
    name: '内部レビュー',
    startDay: 36,
    endDay: 39,
    estimatedHour: 16,
    depth: 3,
  },
  {
    code: 'W7RAOS3G',
    type: 'TASK',
    name: 'レビュー結果反映',
    startDay: 40,
    endDay: 43,
    estimatedHour: 8,
    depth: 3,
  },
  {
    code: 'S8IQ6HHD',
    type: 'DELIVERABLE',
    name: 'EC請求・入金管理領域',
    assigneeUserUuid: 'demo-user-3',
    startDay: 42,
    endDay: 52,
    estimatedHour: 56,
    depth: 2,
  },
  {
    code: 'A1NYOY28',
    type: 'TASK',
    name: '初版作成',
    startDay: 42,
    endDay: 45,
    estimatedHour: 32,
    depth: 3,
  },
  {
    code: 'HEN23H7E',
    type: 'TASK',
    name: '内部レビュー',
    startDay: 46,
    endDay: 48,
    estimatedHour: 16,
    depth: 3,
  },
  {
    code: 'UVBNBEYS',
    type: 'TASK',
    name: 'レビュー結果反映',
    startDay: 49,
    endDay: 52,
    estimatedHour: 8,
    depth: 3,
  },
  {
    code: 'K7VD9PAE',
    type: 'DELIVERABLE_LIST',
    name: 'システム要件定義',
    startDay: 5,
    endDay: 59,
    estimatedHour: 288,
    depth: 1,
  },
  {
    code: 'B6GMS5HN',
    type: 'DELIVERABLE',
    name: '共通',
    assigneeUserUuid: demoUser.uuid,
    startDay: 5,
    endDay: 21,
    estimatedHour: 144,
    depth: 2,
  },
  {
    code: 'XHYT3J86',
    type: 'TASK',
    name: 'システム全体図（1枚ぺら）',
    startDay: 21,
    endDay: 21,
    estimatedHour: 16,
    depth: 3,
  },
  {
    code: 'SAZ18MPE',
    type: 'TASK',
    name: '機能マップ（業務領域単位）',
    startDay: 21,
    endDay: 21,
    estimatedHour: 8,
    depth: 3,
  },
  {
    code: 'BOGT95PU',
    type: 'TASK',
    name: '機能一覧（画面／バッチ／帳票／IF等のカテゴリ別）',
    startDay: 21,
    endDay: 21,
    estimatedHour: 8,
    depth: 3,
  },
  {
    code: 'GWA3ZK0M',
    type: 'TASK',
    name: 'DFD（業務領域単位／主要テーブル＋主要項目）',
    startDay: 21,
    endDay: 21,
    estimatedHour: 8,
    depth: 3,
  },
  {
    code: 'F8617KYW',
    type: 'TASK',
    name: '試用する技術スタック',
    startDay: 21,
    endDay: 21,
    estimatedHour: 8,
    depth: 3,
  },
  {
    code: '9GFUF4XL',
    type: 'TASK',
    name: 'インフラ（AWS構成）',
    startDay: 21,
    endDay: 21,
    estimatedHour: 8,
    depth: 3,
  },
  {
    code: 'VQDXT7ZJ',
    type: 'TASK',
    name: '外部システム連携（対象システムや方式）',
    startDay: 5,
    endDay: 6,
    estimatedHour: 16,
    depth: 3,
  },
  {
    code: 'S9AIIK0R',
    type: 'TASK',
    name: '非機能要件',
    startDay: 7,
    endDay: 8,
    estimatedHour: 16,
    depth: 3,
  },
  {
    code: '6Q1KB6IT',
    type: 'TASK',
    name: 'セキュリティ要件',
    startDay: 10,
    endDay: 12,
    estimatedHour: 16,
    depth: 3,
  },
  {
    code: 'NVHFZLUK',
    type: 'TASK',
    name: 'AI要件',
    startDay: 13,
    endDay: 14,
    estimatedHour: 8,
    depth: 3,
  },
  {
    code: 'KQX54EOR',
    type: 'TASK',
    name: 'レビュー',
    startDay: 15,
    endDay: 17,
    estimatedHour: 16,
    depth: 3,
  },
  {
    code: 'A596DYQG',
    type: 'TASK',
    name: 'レビュー修正',
    startDay: 18,
    endDay: 21,
    estimatedHour: 16,
    depth: 3,
  },
  {
    code: 'KWXY9CD4',
    type: 'DELIVERABLE',
    name: 'EC販売管理領域',
    assigneeUserUuid: 'demo-user-5',
    startDay: 23,
    endDay: 33,
    estimatedHour: 40,
    depth: 2,
  },
  {
    code: 'SKDFHV84',
    type: 'TASK',
    name: '初版作成',
    startDay: 23,
    endDay: 26,
    estimatedHour: 16,
    depth: 3,
  },
  {
    code: 'ADVXA72E',
    type: 'TASK',
    name: '内部レビュー',
    startDay: 27,
    endDay: 29,
    estimatedHour: 16,
    depth: 3,
  },
  {
    code: 'QSH82BWA',
    type: 'TASK',
    name: 'レビュー結果反映',
    startDay: 30,
    endDay: 33,
    estimatedHour: 8,
    depth: 3,
  },
  {
    code: 'JW51OEPN',
    type: 'DELIVERABLE',
    name: 'EC在庫管理領域',
    assigneeUserUuid: 'demo-user-2',
    startDay: 36,
    endDay: 46,
    estimatedHour: 56,
    depth: 2,
  },
  {
    code: 'PFAVV4O8',
    type: 'TASK',
    name: '初版作成',
    startDay: 36,
    endDay: 39,
    estimatedHour: 16,
    depth: 3,
  },
  {
    code: '69JULMD6',
    type: 'TASK',
    name: '内部レビュー',
    startDay: 40,
    endDay: 42,
    estimatedHour: 16,
    depth: 3,
  },
  {
    code: 'RRYCA6JW',
    type: 'TASK',
    name: 'レビュー結果反映',
    startDay: 42,
    endDay: 46,
    estimatedHour: 24,
    depth: 3,
  },
  {
    code: 'QB0832KM',
    type: 'DELIVERABLE',
    name: 'EC請求・入金管理領域',
    assigneeUserUuid: 'demo-user-5',
    startDay: 49,
    endDay: 59,
    estimatedHour: 48,
    depth: 2,
  },
  {
    code: 'NM1AFAN9',
    type: 'TASK',
    name: '初版作成',
    startDay: 49,
    endDay: 51,
    estimatedHour: 32,
    depth: 3,
  },
  {
    code: 'C0QUXA4V',
    type: 'TASK',
    name: '内部レビュー',
    startDay: 52,
    endDay: 54,
    estimatedHour: 8,
    depth: 3,
  },
  {
    code: 'OZVBJWQN',
    type: 'TASK',
    name: 'レビュー結果反映',
    startDay: 55,
    endDay: 59,
    estimatedHour: 8,
    depth: 3,
  },
  {
    code: '5155D8TA',
    type: 'DELIVERABLE',
    name: '業務要件定義書',
    assigneeUserUuid: demoUser.uuid,
    startDay: 20,
    endDay: 74,
    estimatedHour: 120,
    depth: 1,
  },
  {
    code: '79932QZB',
    type: 'TASK',
    name: '初版作成',
    startDay: 20,
    endDay: 37,
    estimatedHour: 80,
    depth: 2,
  },
  {
    code: 'IL1E6DDX',
    type: 'TASK',
    name: '内部レビュー',
    startDay: 38,
    endDay: 55,
    estimatedHour: 24,
    depth: 2,
  },
  {
    code: 'EP4OEZI9',
    type: 'TASK',
    name: 'レビュー結果反映',
    startDay: 56,
    endDay: 74,
    estimatedHour: 16,
    depth: 2,
  },
  {
    code: 'OXU3K4CT',
    type: 'DELIVERABLE',
    name: '画面一覧',
    assigneeUserUuid: 'demo-user-5',
    startDay: 33,
    endDay: 71,
    estimatedHour: 24,
    depth: 1,
  },
  {
    code: '59OFUNY1',
    type: 'TASK',
    name: 'EC販売管理領域',
    startDay: 33,
    endDay: 35,
    estimatedHour: 8,
    depth: 2,
  },
  {
    code: 'TY63246W',
    type: 'TASK',
    name: 'EC在庫管理領域',
    startDay: 37,
    endDay: 37,
    estimatedHour: 8,
    depth: 2,
  },
  {
    code: 'JD0KQQGH',
    type: 'TASK',
    name: 'EC請求・入金管理領域',
    startDay: 33,
    endDay: 71,
    estimatedHour: 8,
    depth: 2,
  },
  {
    code: 'DDT801HF',
    type: 'DELIVERABLE',
    name: '帳票一覧',
    assigneeUserUuid: 'demo-user-2',
    startDay: 55,
    endDay: 74,
    estimatedHour: 80,
    depth: 1,
  },
  {
    code: 'TGK6THPL',
    type: 'TASK',
    name: '項目整理・一覧化',
    startDay: 55,
    endDay: 61,
    estimatedHour: 24,
    depth: 2,
  },
  {
    code: 'RLB4XIIC',
    type: 'TASK',
    name: 'レビュー',
    startDay: 61,
    endDay: 67,
    estimatedHour: 16,
    depth: 2,
  },
  {
    code: '2VLLHC3A',
    type: 'TASK',
    name: 'レビュー結果反映',
    startDay: 68,
    endDay: 74,
    estimatedHour: 40,
    depth: 2,
  },
  {
    code: 'J4LX557H',
    type: 'DELIVERABLE',
    name: 'インターフェース一覧',
    assigneeUserUuid: 'demo-user-3',
    startDay: 55,
    endDay: 75,
    estimatedHour: 40,
    depth: 1,
  },
  {
    code: 'TWBCPJ9F',
    type: 'TASK',
    name: '項目整理・一覧化',
    startDay: 55,
    endDay: 61,
    estimatedHour: 8,
    depth: 2,
  },
  {
    code: 'XKSTUR14',
    type: 'TASK',
    name: 'レビュー',
    startDay: 61,
    endDay: 68,
    estimatedHour: 16,
    depth: 2,
  },
  {
    code: 'UVBZNPUQ',
    type: 'TASK',
    name: 'レビュー結果反映',
    startDay: 69,
    endDay: 75,
    estimatedHour: 16,
    depth: 2,
  },
  {
    code: '6Q138X7T',
    type: 'DELIVERABLE',
    name: 'バッチ処理一覧',
    assigneeUserUuid: demoUser.uuid,
    startDay: 42,
    endDay: 62,
    estimatedHour: 56,
    depth: 1,
  },
  {
    code: 'X8JOJPKO',
    type: 'TASK',
    name: '項目整理・一覧化',
    startDay: 42,
    endDay: 49,
    estimatedHour: 24,
    depth: 2,
  },
  {
    code: 'LPKMRW0N',
    type: 'TASK',
    name: 'レビュー',
    startDay: 49,
    endDay: 55,
    estimatedHour: 16,
    depth: 2,
  },
  {
    code: 'P40YKL6A',
    type: 'TASK',
    name: 'レビュー結果反映',
    startDay: 56,
    endDay: 62,
    estimatedHour: 16,
    depth: 2,
  },
  {
    code: 'NU2M6QD1',
    type: 'DELIVERABLE',
    name: 'データ一覧',
    assigneeUserUuid: 'demo-user-5',
    startDay: 17,
    endDay: 42,
    estimatedHour: 96,
    depth: 1,
  },
  {
    code: 'CGJ0EWES',
    type: 'TASK',
    name: '項目整理・一覧化',
    startDay: 17,
    endDay: 24,
    estimatedHour: 40,
    depth: 2,
  },
  {
    code: '3FYON1S4',
    type: 'TASK',
    name: 'レビュー',
    startDay: 25,
    endDay: 33,
    estimatedHour: 16,
    depth: 2,
  },
  {
    code: 'TQX7YGME',
    type: 'TASK',
    name: 'レビュー結果反映',
    startDay: 34,
    endDay: 42,
    estimatedHour: 40,
    depth: 2,
  },
  {
    code: '3V5WFLYA',
    type: 'DELIVERABLE',
    name: '顧客レビュー',
    assigneeUserUuid: 'demo-user-2',
    startDay: 0,
    endDay: 65,
    estimatedHour: 160,
    depth: 1,
  },
  {
    code: 'GSP7TYF9',
    type: 'TASK',
    name: '業務フロー',
    startDay: 36,
    endDay: 52,
    estimatedHour: 16,
    depth: 2,
  },
  {
    code: '3MLG7VFJ',
    type: 'TASK',
    name: '業務要件定義書',
    startDay: 0,
    endDay: 8,
    estimatedHour: 32,
    depth: 2,
  },
  {
    code: '5CLP4H1P',
    type: 'TASK',
    name: 'システム要件定義書',
    startDay: 9,
    endDay: 18,
    estimatedHour: 16,
    depth: 2,
  },
  {
    code: '5VM22T1X',
    type: 'TASK',
    name: '画面一覧',
    startDay: 19,
    endDay: 27,
    estimatedHour: 32,
    depth: 2,
  },
  {
    code: 'TUV8JMEH',
    type: 'TASK',
    name: '機能一覧',
    startDay: 28,
    endDay: 36,
    estimatedHour: 8,
    depth: 2,
  },
  {
    code: '53YOMZTW',
    type: 'TASK',
    name: 'インターフェース一覧',
    startDay: 37,
    endDay: 45,
    estimatedHour: 8,
    depth: 2,
  },
  {
    code: 'NA42GY3I',
    type: 'TASK',
    name: '帳票一覧',
    startDay: 46,
    endDay: 55,
    estimatedHour: 8,
    depth: 2,
  },
  {
    code: 'GYSP8VYG',
    type: 'TASK',
    name: 'データ一覧',
    startDay: 56,
    endDay: 65,
    estimatedHour: 40,
    depth: 2,
  },
  {
    code: 'KWALN8LS',
    type: 'DELIVERABLE',
    name: '要件定義フェーズ完了報告会',
    assigneeUserUuid: 'demo-user-3',
    startDay: 68,
    endDay: 81,
    estimatedHour: 32,
    depth: 1,
  },
  {
    code: '08PLVM1T',
    type: 'TASK',
    name: '会議準備',
    startDay: 68,
    endDay: 71,
    estimatedHour: 16,
    depth: 2,
  },
  {
    code: 'TVLPOWKK',
    type: 'TASK',
    name: '会議実施',
    startDay: 74,
    endDay: 76,
    estimatedHour: 8,
    depth: 2,
  },
  {
    code: 'BC7PN8JT',
    type: 'TASK',
    name: '議事録配布',
    startDay: 77,
    endDay: 81,
    estimatedHour: 8,
    depth: 2,
  },

  // 要件定義（day 0〜93）完了後に続く後続工程。実プロジェクトの計画を模して
  // ウォーターフォールに並べ、設計→開発→各テスト→リリースが前工程の完了を
  // 待って始まる形にしている。データ移行・AI機能構築・運用準備・ユーザー
  // トレーニングは本流と並走する工程なので、期間を重ねて配置している。

  {
    code: 'FEK8H4AS',
    type: 'PROCESS',
    name: 'ベンダー選定',
    startDay: 56,
    endDay: 105,
    estimatedHour: 160,
    depth: 0,
  },
  {
    code: 'AP4Y2MKS',
    type: 'DELIVERABLE',
    name: 'ベンダー選定計画書',
    assigneeUserUuid: demoUser.uuid,
    startDay: 56,
    endDay: 103,
    estimatedHour: 40,
    depth: 1,
  },
  {
    code: 'X1MA52PX',
    type: 'TASK',
    name: '方針検討',
    startDay: 56,
    endDay: 67,
    estimatedHour: 8,
    depth: 2,
  },
  {
    code: '6P485MPD',
    type: 'TASK',
    name: '計画書作成',
    startDay: 68,
    endDay: 80,
    estimatedHour: 16,
    depth: 2,
  },
  {
    code: 'LD3T94AC',
    type: 'TASK',
    name: 'レビュー・承認',
    startDay: 80,
    endDay: 103,
    estimatedHour: 16,
    depth: 2,
  },
  {
    code: 'IFCLI64H',
    type: 'DELIVERABLE',
    name: 'RFP（提案依頼書）',
    assigneeUserUuid: 'demo-user-5',
    startDay: 94,
    endDay: 100,
    estimatedHour: 24,
    depth: 1,
  },
  {
    code: '18X5DGOU',
    type: 'TASK',
    name: 'RFP項目整理',
    startDay: 94,
    endDay: 98,
    estimatedHour: 8,
    depth: 2,
  },
  {
    code: 'SVVN1LR6',
    type: 'TASK',
    name: 'RFP作成',
    startDay: 94,
    endDay: 99,
    estimatedHour: 8,
    depth: 2,
  },
  {
    code: '8NTP94OQ',
    type: 'TASK',
    name: 'RFPレビュー・確定',
    startDay: 97,
    endDay: 100,
    estimatedHour: 8,
    depth: 2,
  },
  {
    code: '8XPNI6AZ',
    type: 'DELIVERABLE',
    name: 'ベンダー提案書',
    assigneeUserUuid: 'demo-user-2',
    startDay: 96,
    endDay: 98,
    estimatedHour: 32,
    depth: 1,
  },
  {
    code: '5KVAYZR7',
    type: 'TASK',
    name: '提案依頼・説明会実施',
    startDay: 96,
    endDay: 96,
    estimatedHour: 8,
    depth: 2,
  },
  {
    code: '6FD25MRH',
    type: 'TASK',
    name: '提案書受領',
    startDay: 97,
    endDay: 97,
    estimatedHour: 8,
    depth: 2,
  },
  {
    code: 'NOR9GKXM',
    type: 'TASK',
    name: '提案内容整理',
    startDay: 97,
    endDay: 98,
    estimatedHour: 16,
    depth: 2,
  },
  {
    code: '74I5JM04',
    type: 'DELIVERABLE',
    name: 'ベンダー評価レポート',
    assigneeUserUuid: 'demo-user-3',
    startDay: 99,
    endDay: 101,
    estimatedHour: 32,
    depth: 1,
  },
  {
    code: 'XT2E7JX4',
    type: 'TASK',
    name: '評価基準策定',
    startDay: 99,
    endDay: 99,
    estimatedHour: 8,
    depth: 2,
  },
  {
    code: '3RIJFDH3',
    type: 'TASK',
    name: '評価実施',
    startDay: 100,
    endDay: 100,
    estimatedHour: 8,
    depth: 2,
  },
  {
    code: 'MQ7YLTYR',
    type: 'TASK',
    name: '評価レポート作成',
    startDay: 101,
    endDay: 101,
    estimatedHour: 16,
    depth: 2,
  },
  {
    code: 'CN3O1YLW',
    type: 'DELIVERABLE',
    name: 'ベンダー選定決定書・契約',
    assigneeUserUuid: demoUser.uuid,
    startDay: 102,
    endDay: 105,
    estimatedHour: 32,
    depth: 1,
  },
  {
    code: 'CENV5OIN',
    type: 'TASK',
    name: '選定会議実施',
    startDay: 102,
    endDay: 102,
    estimatedHour: 8,
    depth: 2,
  },
  {
    code: 'CLW9AD2T',
    type: 'TASK',
    name: '選定結果決定書作成',
    startDay: 103,
    endDay: 104,
    estimatedHour: 16,
    depth: 2,
  },
  {
    code: '3YX4EAY5',
    type: 'TASK',
    name: '契約締結',
    startDay: 105,
    endDay: 105,
    estimatedHour: 8,
    depth: 2,
  },

  {
    code: 'VAQ4AFA5',
    type: 'PROCESS',
    name: '基本設計',
    startDay: 84,
    endDay: 175,
    estimatedHour: 1272,
    depth: 0,
  },
  {
    code: 'YQNPBMY0',
    type: 'DELIVERABLE',
    name: '基本設計書（共通）（画面基本設計）',
    assigneeUserUuid: 'demo-user-5',
    startDay: 84,
    endDay: 111,
    estimatedHour: 104,
    depth: 1,
  },
  {
    code: 'KN6AE2R1',
    type: 'TASK',
    name: '画面レイアウト定義',
    startDay: 84,
    endDay: 100,
    estimatedHour: 40,
    depth: 2,
  },
  {
    code: '96FW4VBI',
    type: 'TASK',
    name: '画面遷移図作成',
    startDay: 101,
    endDay: 111,
    estimatedHour: 64,
    depth: 2,
  },
  {
    code: 'ZZUNJF7Y',
    type: 'DELIVERABLE',
    name: '基本設計書（共通）（DB基本設計）',
    assigneeUserUuid: 'demo-user-2',
    startDay: 101,
    endDay: 121,
    estimatedHour: 104,
    depth: 1,
  },
  {
    code: 'VSG4VSTE',
    type: 'TASK',
    name: 'ER図作成',
    startDay: 101,
    endDay: 111,
    estimatedHour: 56,
    depth: 2,
  },
  {
    code: 'SOSTH9TG',
    type: 'TASK',
    name: 'テーブル定義書作成',
    startDay: 112,
    endDay: 121,
    estimatedHour: 48,
    depth: 2,
  },
  {
    code: 'M5SQSMI7',
    type: 'DELIVERABLE',
    name: '基本設計書（共通）（機能基本設計）',
    assigneeUserUuid: 'demo-user-3',
    startDay: 112,
    endDay: 133,
    estimatedHour: 104,
    depth: 1,
  },
  {
    code: '1Z2AZMLK',
    type: 'TASK',
    name: '初版作成',
    startDay: 112,
    endDay: 117,
    estimatedHour: 32,
    depth: 2,
  },
  {
    code: 'ZWCF556T',
    type: 'TASK',
    name: '内部レビュー',
    startDay: 119,
    endDay: 125,
    estimatedHour: 40,
    depth: 2,
  },
  {
    code: 'II9IGVCV',
    type: 'TASK',
    name: 'レビュー結果反映',
    startDay: 125,
    endDay: 133,
    estimatedHour: 32,
    depth: 2,
  },
  {
    code: 'EJXOKGOL',
    type: 'DELIVERABLE',
    name: '基本設計書（共通）（外部インターフェース基本設計）',
    assigneeUserUuid: demoUser.uuid,
    startDay: 112,
    endDay: 125,
    estimatedHour: 64,
    depth: 1,
  },
  {
    code: '7FZ5C3T0',
    type: 'TASK',
    name: '初版作成',
    startDay: 112,
    endDay: 115,
    estimatedHour: 16,
    depth: 2,
  },
  {
    code: 'LXI343FQ',
    type: 'TASK',
    name: '内部レビュー',
    startDay: 116,
    endDay: 121,
    estimatedHour: 24,
    depth: 2,
  },
  {
    code: 'LG8AT699',
    type: 'TASK',
    name: 'レビュー結果反映',
    startDay: 121,
    endDay: 125,
    estimatedHour: 24,
    depth: 2,
  },
  {
    code: 'ILHFUZFO',
    type: 'DELIVERABLE',
    name: '基本設計書（共通）（非機能設計（性能・可用性・セキュリティ））',
    assigneeUserUuid: 'demo-user-5',
    startDay: 117,
    endDay: 133,
    estimatedHour: 80,
    depth: 1,
  },
  {
    code: 'H94WLAAG',
    type: 'TASK',
    name: '初版作成',
    startDay: 117,
    endDay: 122,
    estimatedHour: 24,
    depth: 2,
  },
  {
    code: '3VYX3GBM',
    type: 'TASK',
    name: '内部レビュー',
    startDay: 123,
    endDay: 126,
    estimatedHour: 24,
    depth: 2,
  },
  {
    code: 'W17KF303',
    type: 'TASK',
    name: 'レビュー結果反映',
    startDay: 128,
    endDay: 133,
    estimatedHour: 32,
    depth: 2,
  },
  {
    code: 'X6HGMQBZ',
    type: 'DELIVERABLE',
    name: '基本設計書（共通）（レビュー）',
    assigneeUserUuid: 'demo-user-2',
    startDay: 133,
    endDay: 139,
    estimatedHour: 40,
    depth: 1,
  },
  {
    code: '8V5MQGG9',
    type: 'TASK',
    name: 'レビュー会実施',
    startDay: 133,
    endDay: 134,
    estimatedHour: 16,
    depth: 2,
  },
  {
    code: 'GX1C85DI',
    type: 'TASK',
    name: '指摘事項整理',
    startDay: 135,
    endDay: 136,
    estimatedHour: 8,
    depth: 2,
  },
  {
    code: 'QFNWVQVY',
    type: 'TASK',
    name: 'コメント確定',
    startDay: 138,
    endDay: 139,
    estimatedHour: 16,
    depth: 2,
  },
  {
    code: 'L8YO91SZ',
    type: 'DELIVERABLE',
    name: '基本設計書（共通）（レビュー結果反映）',
    assigneeUserUuid: 'demo-user-3',
    startDay: 140,
    endDay: 147,
    estimatedHour: 32,
    depth: 1,
  },
  {
    code: 'KXQF4RV5',
    type: 'TASK',
    name: '修正方針検討',
    startDay: 140,
    endDay: 142,
    estimatedHour: 8,
    depth: 2,
  },
  {
    code: '3YNXK8G0',
    type: 'TASK',
    name: '修正実施',
    startDay: 143,
    endDay: 143,
    estimatedHour: 16,
    depth: 2,
  },
  {
    code: 'D3U9N7U6',
    type: 'TASK',
    name: '修正内容確認',
    startDay: 144,
    endDay: 147,
    estimatedHour: 8,
    depth: 2,
  },
  {
    code: 'WVUB1L58',
    type: 'DELIVERABLE',
    name: '基本設計書（EC販売管理領域）（基本設計書）',
    assigneeUserUuid: demoUser.uuid,
    startDay: 90,
    endDay: 147,
    estimatedHour: 208,
    depth: 1,
  },
  {
    code: 'NLK83F91',
    type: 'TASK',
    name: '初版作成',
    startDay: 90,
    endDay: 108,
    estimatedHour: 32,
    depth: 2,
  },
  {
    code: 'XQL88WHM',
    type: 'TASK',
    name: '内部レビュー',
    startDay: 109,
    endDay: 126,
    estimatedHour: 80,
    depth: 2,
  },
  {
    code: 'XBHUYQT1',
    type: 'TASK',
    name: 'レビュー結果反映',
    startDay: 128,
    endDay: 147,
    estimatedHour: 96,
    depth: 2,
  },
  {
    code: 'Q2BI4N7G',
    type: 'DELIVERABLE',
    name: '基本設計書（EC在庫管理領域）（基本設計書）',
    assigneeUserUuid: 'demo-user-5',
    startDay: 100,
    endDay: 150,
    estimatedHour: 240,
    depth: 1,
  },
  {
    code: 'FR1GUW4U',
    type: 'TASK',
    name: '初版作成',
    startDay: 100,
    endDay: 116,
    estimatedHour: 80,
    depth: 2,
  },
  {
    code: 'CDJDO8WB',
    type: 'TASK',
    name: '内部レビュー',
    startDay: 116,
    endDay: 133,
    estimatedHour: 80,
    depth: 2,
  },
  {
    code: 'DWNSQEQP',
    type: 'TASK',
    name: 'レビュー結果反映',
    startDay: 133,
    endDay: 150,
    estimatedHour: 80,
    depth: 2,
  },
  {
    code: 'BYEIDB48',
    type: 'DELIVERABLE',
    name: '基本設計書（EC請求・入金管理領域）（基本設計書）',
    assigneeUserUuid: 'demo-user-2',
    startDay: 112,
    endDay: 153,
    estimatedHour: 192,
    depth: 1,
  },
  {
    code: 'Y00E835Z',
    type: 'TASK',
    name: '初版作成',
    startDay: 112,
    endDay: 125,
    estimatedHour: 64,
    depth: 2,
  },
  {
    code: 'MVL9CXK6',
    type: 'TASK',
    name: '内部レビュー',
    startDay: 125,
    endDay: 139,
    estimatedHour: 64,
    depth: 2,
  },
  {
    code: 'ZQ10IGIZ',
    type: 'TASK',
    name: 'レビュー結果反映',
    startDay: 139,
    endDay: 153,
    estimatedHour: 64,
    depth: 2,
  },
  {
    code: 'NW25AOI1',
    type: 'DELIVERABLE',
    name: '顧客レビュー・承認（顧客レビュー）',
    assigneeUserUuid: 'demo-user-3',
    startDay: 154,
    endDay: 175,
    estimatedHour: 104,
    depth: 1,
  },
  {
    code: 'YK40TYJY',
    type: 'TASK',
    name: 'レビュー資料準備',
    startDay: 154,
    endDay: 160,
    estimatedHour: 32,
    depth: 2,
  },
  {
    code: 'C32ZQR2P',
    type: 'TASK',
    name: '顧客レビュー会実施',
    startDay: 161,
    endDay: 167,
    estimatedHour: 32,
    depth: 2,
  },
  {
    code: 'GKU5WGSA',
    type: 'TASK',
    name: '指摘事項対応・承認取得',
    startDay: 168,
    endDay: 175,
    estimatedHour: 40,
    depth: 2,
  },

  {
    code: 'V45NM19E',
    type: 'PROCESS',
    name: '詳細設計〜開発・UT',
    startDay: 154,
    endDay: 259,
    estimatedHour: 1160,
    depth: 0,
  },
  {
    code: 'HIKE4ORH',
    type: 'DELIVERABLE',
    name: '詳細設計書（EC販売管理領域）',
    assigneeUserUuid: demoUser.uuid,
    startDay: 154,
    endDay: 200,
    estimatedHour: 192,
    depth: 1,
  },
  {
    code: 'XGDZCNLH',
    type: 'TASK',
    name: '初版作成',
    startDay: 154,
    endDay: 169,
    estimatedHour: 64,
    depth: 2,
  },
  {
    code: 'Q85J3HL0',
    type: 'TASK',
    name: '内部レビュー',
    startDay: 170,
    endDay: 183,
    estimatedHour: 64,
    depth: 2,
  },
  {
    code: '81MPK0Y6',
    type: 'TASK',
    name: 'レビュー結果反映',
    startDay: 184,
    endDay: 200,
    estimatedHour: 64,
    depth: 2,
  },
  {
    code: 'CSFSMF6M',
    type: 'DELIVERABLE',
    name: '詳細設計書（EC在庫管理領域）',
    assigneeUserUuid: 'demo-user-5',
    startDay: 165,
    endDay: 212,
    estimatedHour: 192,
    depth: 1,
  },
  {
    code: '5ORSYMPQ',
    type: 'TASK',
    name: '初版作成',
    startDay: 165,
    endDay: 180,
    estimatedHour: 64,
    depth: 2,
  },
  {
    code: '6SWHPVOJ',
    type: 'TASK',
    name: '内部レビュー',
    startDay: 181,
    endDay: 195,
    estimatedHour: 64,
    depth: 2,
  },
  {
    code: '93E3F34F',
    type: 'TASK',
    name: 'レビュー結果反映',
    startDay: 196,
    endDay: 212,
    estimatedHour: 64,
    depth: 2,
  },
  {
    code: '3IBKIDM6',
    type: 'DELIVERABLE',
    name: '詳細設計書（EC請求・入金管理領域）',
    assigneeUserUuid: 'demo-user-2',
    startDay: 178,
    endDay: 223,
    estimatedHour: 192,
    depth: 1,
  },
  {
    code: 'S2RCICYN',
    type: 'TASK',
    name: '初版作成',
    startDay: 178,
    endDay: 192,
    estimatedHour: 64,
    depth: 2,
  },
  {
    code: '32YHYKW4',
    type: 'TASK',
    name: '内部レビュー',
    startDay: 193,
    endDay: 208,
    estimatedHour: 64,
    depth: 2,
  },
  {
    code: 'P6KBMQYA',
    type: 'TASK',
    name: 'レビュー結果反映',
    startDay: 209,
    endDay: 223,
    estimatedHour: 64,
    depth: 2,
  },
  {
    code: 'KPJCO0YJ',
    type: 'DELIVERABLE',
    name: '実装（コーディング）',
    assigneeUserUuid: 'demo-user-3',
    startDay: 178,
    endDay: 248,
    estimatedHour: 312,
    depth: 1,
  },
  {
    code: 'VWZNXJS3',
    type: 'TASK',
    name: '実装計画・タスク分割',
    startDay: 178,
    endDay: 200,
    estimatedHour: 96,
    depth: 2,
  },
  {
    code: 'PWAH5MIG',
    type: 'TASK',
    name: '実装',
    startDay: 201,
    endDay: 223,
    estimatedHour: 120,
    depth: 2,
  },
  {
    code: 'LGSOFPL9',
    type: 'TASK',
    name: 'コードレビュー',
    startDay: 224,
    endDay: 248,
    estimatedHour: 96,
    depth: 2,
  },
  {
    code: 'N1Y48LYO',
    type: 'DELIVERABLE',
    name: '単体テスト仕様書作成',
    assigneeUserUuid: demoUser.uuid,
    startDay: 201,
    endDay: 235,
    estimatedHour: 168,
    depth: 1,
  },
  {
    code: 'T344I8JX',
    type: 'TASK',
    name: 'テスト観点整理',
    startDay: 201,
    endDay: 212,
    estimatedHour: 56,
    depth: 2,
  },
  {
    code: 'PUWR6ZV0',
    type: 'TASK',
    name: 'テストケース作成',
    startDay: 212,
    endDay: 223,
    estimatedHour: 56,
    depth: 2,
  },
  {
    code: 'Q1L0Z0KG',
    type: 'TASK',
    name: 'レビュー',
    startDay: 223,
    endDay: 235,
    estimatedHour: 56,
    depth: 2,
  },
  {
    code: '5ZTKW7FT',
    type: 'DELIVERABLE',
    name: '単体テスト実施',
    assigneeUserUuid: 'demo-user-5',
    startDay: 236,
    endDay: 259,
    estimatedHour: 104,
    depth: 1,
  },
  {
    code: 'PJKFCNJM',
    type: 'TASK',
    name: 'テスト実施',
    startDay: 236,
    endDay: 243,
    estimatedHour: 32,
    depth: 2,
  },
  {
    code: 'DXLTX8LS',
    type: 'TASK',
    name: '結果整理・障害登録',
    startDay: 244,
    endDay: 251,
    estimatedHour: 40,
    depth: 2,
  },
  {
    code: '1OUD9R18',
    type: 'TASK',
    name: '障害修正確認',
    startDay: 251,
    endDay: 259,
    estimatedHour: 32,
    depth: 2,
  },

  {
    code: 'NBYDJZK0',
    type: 'PROCESS',
    name: '内部結合テスト',
    startDay: 238,
    endDay: 287,
    estimatedHour: 248,
    depth: 0,
  },
  {
    code: 'XJR9JCKK',
    type: 'DELIVERABLE',
    name: '内部結合テスト仕様書作成',
    assigneeUserUuid: 'demo-user-2',
    startDay: 238,
    endDay: 258,
    estimatedHour: 104,
    depth: 1,
  },
  {
    code: 'HSUOLUDG',
    type: 'TASK',
    name: 'テスト観点整理',
    startDay: 238,
    endDay: 244,
    estimatedHour: 32,
    depth: 2,
  },
  {
    code: 'AG8Y1CLC',
    type: 'TASK',
    name: 'テストケース作成',
    startDay: 245,
    endDay: 251,
    estimatedHour: 40,
    depth: 2,
  },
  {
    code: '2DN8DNC7',
    type: 'TASK',
    name: 'レビュー',
    startDay: 251,
    endDay: 258,
    estimatedHour: 32,
    depth: 2,
  },
  {
    code: 'CFOZD88B',
    type: 'DELIVERABLE',
    name: '内部結合テスト実施',
    assigneeUserUuid: 'demo-user-3',
    startDay: 259,
    endDay: 276,
    estimatedHour: 96,
    depth: 1,
  },
  {
    code: 'IUSFKCYJ',
    type: 'TASK',
    name: 'テスト実施',
    startDay: 259,
    endDay: 263,
    estimatedHour: 32,
    depth: 2,
  },
  {
    code: 'B8A4T1LO',
    type: 'TASK',
    name: '結果整理・障害登録',
    startDay: 264,
    endDay: 269,
    estimatedHour: 32,
    depth: 2,
  },
  {
    code: 'ZQTALH6U',
    type: 'TASK',
    name: '障害修正確認',
    startDay: 270,
    endDay: 276,
    estimatedHour: 32,
    depth: 2,
  },
  {
    code: 'NGII28Y9',
    type: 'DELIVERABLE',
    name: 'バグ修正・再テスト',
    assigneeUserUuid: demoUser.uuid,
    startDay: 277,
    endDay: 287,
    estimatedHour: 48,
    depth: 1,
  },
  {
    code: 'J8WM2V5A',
    type: 'TASK',
    name: '障害内容確認・対応方針検討',
    startDay: 277,
    endDay: 279,
    estimatedHour: 16,
    depth: 2,
  },
  {
    code: 'SVIQQCB3',
    type: 'TASK',
    name: 'バグ修正',
    startDay: 280,
    endDay: 282,
    estimatedHour: 16,
    depth: 2,
  },
  {
    code: 'L91LKD9X',
    type: 'TASK',
    name: '再テスト実施',
    startDay: 283,
    endDay: 287,
    estimatedHour: 16,
    depth: 2,
  },

  {
    code: 'O07914VQ',
    type: 'PROCESS',
    name: '外部結合テスト',
    startDay: 280,
    endDay: 315,
    estimatedHour: 216,
    depth: 0,
  },
  {
    code: '2DVBO4T8',
    type: 'DELIVERABLE',
    name: '外部結合テスト仕様書',
    assigneeUserUuid: 'demo-user-5',
    startDay: 280,
    endDay: 285,
    estimatedHour: 40,
    depth: 1,
  },
  {
    code: 'ZRE78MCU',
    type: 'TASK',
    name: 'テスト観点整理',
    startDay: 280,
    endDay: 281,
    estimatedHour: 16,
    depth: 2,
  },
  {
    code: 'E8ZSXS3I',
    type: 'TASK',
    name: 'テストケース作成',
    startDay: 281,
    endDay: 283,
    estimatedHour: 8,
    depth: 2,
  },
  {
    code: 'EY2UOAVS',
    type: 'TASK',
    name: 'レビュー',
    startDay: 283,
    endDay: 285,
    estimatedHour: 16,
    depth: 2,
  },
  {
    code: '389PCVCU',
    type: 'DELIVERABLE',
    name: '外部結合テスト打鍵（販売管理システム）',
    assigneeUserUuid: 'demo-user-2',
    startDay: 286,
    endDay: 291,
    estimatedHour: 32,
    depth: 1,
  },
  {
    code: 'I517IVO8',
    type: 'TASK',
    name: 'テスト実施',
    startDay: 286,
    endDay: 287,
    estimatedHour: 8,
    depth: 2,
  },
  {
    code: 'OMP9O7XJ',
    type: 'TASK',
    name: '結果整理・障害登録',
    startDay: 287,
    endDay: 289,
    estimatedHour: 16,
    depth: 2,
  },
  {
    code: 'DQKHBB93',
    type: 'TASK',
    name: '障害修正確認',
    startDay: 289,
    endDay: 291,
    estimatedHour: 8,
    depth: 2,
  },
  {
    code: 'S90STNL4',
    type: 'DELIVERABLE',
    name: '外部結合テスト打鍵（在庫管理システム）',
    assigneeUserUuid: 'demo-user-3',
    startDay: 291,
    endDay: 296,
    estimatedHour: 40,
    depth: 1,
  },
  {
    code: '0K5VSL7Q',
    type: 'TASK',
    name: 'テスト実施',
    startDay: 291,
    endDay: 292,
    estimatedHour: 16,
    depth: 2,
  },
  {
    code: 'XSHY7PFK',
    type: 'TASK',
    name: '結果整理・障害登録',
    startDay: 293,
    endDay: 294,
    estimatedHour: 8,
    depth: 2,
  },
  {
    code: 'A6JSR6HJ',
    type: 'TASK',
    name: '障害修正確認',
    startDay: 295,
    endDay: 296,
    estimatedHour: 16,
    depth: 2,
  },
  {
    code: '5CD4W9C6',
    type: 'DELIVERABLE',
    name: '外部結合テスト打鍵（請求入金管理システム）',
    assigneeUserUuid: demoUser.uuid,
    startDay: 297,
    endDay: 303,
    estimatedHour: 32,
    depth: 1,
  },
  {
    code: 'L2W5MV16',
    type: 'TASK',
    name: 'テスト実施',
    startDay: 297,
    endDay: 298,
    estimatedHour: 8,
    depth: 2,
  },
  {
    code: '4S18H0Y0',
    type: 'TASK',
    name: '結果整理・障害登録',
    startDay: 299,
    endDay: 301,
    estimatedHour: 16,
    depth: 2,
  },
  {
    code: 'D58IN9IU',
    type: 'TASK',
    name: '障害修正確認',
    startDay: 302,
    endDay: 303,
    estimatedHour: 8,
    depth: 2,
  },
  {
    code: '75E6J8LR',
    type: 'DELIVERABLE',
    name: '再テスト',
    assigneeUserUuid: 'demo-user-5',
    startDay: 304,
    endDay: 309,
    estimatedHour: 40,
    depth: 1,
  },
  {
    code: '9DBQRDXF',
    type: 'TASK',
    name: 'テスト実施',
    startDay: 304,
    endDay: 304,
    estimatedHour: 16,
    depth: 2,
  },
  {
    code: 'U2HXN6ER',
    type: 'TASK',
    name: '結果整理・障害登録',
    startDay: 305,
    endDay: 306,
    estimatedHour: 8,
    depth: 2,
  },
  {
    code: 'VNR2QR9J',
    type: 'TASK',
    name: '障害修正確認',
    startDay: 307,
    endDay: 309,
    estimatedHour: 16,
    depth: 2,
  },
  {
    code: '499KHNWA',
    type: 'DELIVERABLE',
    name: '外部結合テスト結果報告書',
    assigneeUserUuid: 'demo-user-2',
    startDay: 310,
    endDay: 315,
    estimatedHour: 32,
    depth: 1,
  },
  {
    code: 'YX67MIEW',
    type: 'TASK',
    name: '結果集計',
    startDay: 310,
    endDay: 310,
    estimatedHour: 8,
    depth: 2,
  },
  {
    code: 'IB7YXKBF',
    type: 'TASK',
    name: '報告書作成',
    startDay: 311,
    endDay: 312,
    estimatedHour: 16,
    depth: 2,
  },
  {
    code: 'HWNU4DZ4',
    type: 'TASK',
    name: '報告会実施',
    startDay: 313,
    endDay: 315,
    estimatedHour: 8,
    depth: 2,
  },

  {
    code: 'F9F785EF',
    type: 'PROCESS',
    name: 'システムテスト',
    startDay: 308,
    endDay: 350,
    estimatedHour: 320,
    depth: 0,
  },
  {
    code: 'YQG7IVRW',
    type: 'DELIVERABLE',
    name: 'サイクルテスト仕様書',
    assigneeUserUuid: 'demo-user-3',
    startDay: 308,
    endDay: 340,
    estimatedHour: 168,
    depth: 1,
  },
  {
    code: 'PL2VKOVA',
    type: 'TASK',
    name: 'テスト観点整理',
    startDay: 308,
    endDay: 318,
    estimatedHour: 56,
    depth: 2,
  },
  {
    code: '4MQXN0A3',
    type: 'TASK',
    name: 'テストケース作成',
    startDay: 319,
    endDay: 329,
    estimatedHour: 56,
    depth: 2,
  },
  {
    code: 'YZMPQ30H',
    type: 'TASK',
    name: 'レビュー',
    startDay: 330,
    endDay: 340,
    estimatedHour: 56,
    depth: 2,
  },
  {
    code: 'IEGQNEKY',
    type: 'DELIVERABLE',
    name: '性能テスト仕様書',
    assigneeUserUuid: demoUser.uuid,
    startDay: 330,
    endDay: 340,
    estimatedHour: 56,
    depth: 1,
  },
  {
    code: '5FLLNXJ1',
    type: 'TASK',
    name: 'テスト観点整理',
    startDay: 330,
    endDay: 332,
    estimatedHour: 16,
    depth: 2,
  },
  {
    code: 'L9P25TRF',
    type: 'TASK',
    name: 'テストケース作成',
    startDay: 333,
    endDay: 335,
    estimatedHour: 16,
    depth: 2,
  },
  {
    code: '47U3IFBH',
    type: 'TASK',
    name: 'レビュー',
    startDay: 336,
    endDay: 340,
    estimatedHour: 24,
    depth: 2,
  },
  {
    code: 'HHBO21M2',
    type: 'DELIVERABLE',
    name: '障害テスト仕様書',
    assigneeUserUuid: 'demo-user-5',
    startDay: 342,
    endDay: 350,
    estimatedHour: 48,
    depth: 1,
  },
  {
    code: 'Z9G77XNV',
    type: 'TASK',
    name: 'テスト観点整理',
    startDay: 342,
    endDay: 344,
    estimatedHour: 16,
    depth: 2,
  },
  {
    code: 'EMD9U86V',
    type: 'TASK',
    name: 'テストケース作成',
    startDay: 344,
    endDay: 347,
    estimatedHour: 8,
    depth: 2,
  },
  {
    code: 'D9UKX7SU',
    type: 'TASK',
    name: 'レビュー',
    startDay: 347,
    endDay: 350,
    estimatedHour: 24,
    depth: 2,
  },
  {
    code: 'BD20FKBQ',
    type: 'DELIVERABLE',
    name: 'セキュリティテスト仕様書',
    assigneeUserUuid: 'demo-user-2',
    startDay: 342,
    endDay: 350,
    estimatedHour: 48,
    depth: 1,
  },
  {
    code: 'QFCCN6HW',
    type: 'TASK',
    name: 'テスト観点整理',
    startDay: 342,
    endDay: 344,
    estimatedHour: 16,
    depth: 2,
  },
  {
    code: 'M4W2AUNH',
    type: 'TASK',
    name: 'テストケース作成',
    startDay: 344,
    endDay: 347,
    estimatedHour: 8,
    depth: 2,
  },
  {
    code: 'X7AIMVRK',
    type: 'TASK',
    name: 'レビュー',
    startDay: 347,
    endDay: 350,
    estimatedHour: 24,
    depth: 2,
  },

  {
    code: 'UJ0CRUG7',
    type: 'PROCESS',
    name: 'UAT',
    startDay: 336,
    endDay: 371,
    estimatedHour: 192,
    depth: 0,
  },
  {
    code: '6WDRX1O3',
    type: 'DELIVERABLE',
    name: 'UATシナリオ・実施',
    assigneeUserUuid: 'demo-user-3',
    startDay: 336,
    endDay: 371,
    estimatedHour: 192,
    depth: 1,
  },
  {
    code: 'F7P5A357',
    type: 'TASK',
    name: '初版作成',
    startDay: 336,
    endDay: 347,
    estimatedHour: 64,
    depth: 2,
  },
  {
    code: '5IIXXQAC',
    type: 'TASK',
    name: '内部レビュー',
    startDay: 347,
    endDay: 359,
    estimatedHour: 64,
    depth: 2,
  },
  {
    code: 'ZO2O2WX1',
    type: 'TASK',
    name: 'レビュー結果反映',
    startDay: 360,
    endDay: 371,
    estimatedHour: 64,
    depth: 2,
  },

  {
    code: 'AGVV4V7A',
    type: 'PROCESS',
    name: 'データ移行',
    startDay: 126,
    endDay: 315,
    estimatedHour: 1176,
    depth: 0,
  },
  {
    code: '2I4YK2ET',
    type: 'DELIVERABLE',
    name: 'データ移行（計画・設計）（データ移行計画・設計書）',
    assigneeUserUuid: demoUser.uuid,
    startDay: 126,
    endDay: 183,
    estimatedHour: 312,
    depth: 1,
  },
  {
    code: 'UWQI7VMX',
    type: 'TASK',
    name: '移行方針検討',
    startDay: 126,
    endDay: 144,
    estimatedHour: 96,
    depth: 2,
  },
  {
    code: 'EYH1KLS3',
    type: 'TASK',
    name: '移行計画書作成',
    startDay: 145,
    endDay: 163,
    estimatedHour: 96,
    depth: 2,
  },
  {
    code: 'OUPUZ7FB',
    type: 'TASK',
    name: '移行設計書作成',
    startDay: 164,
    endDay: 183,
    estimatedHour: 120,
    depth: 2,
  },
  {
    code: 'NEIGL2HI',
    type: 'DELIVERABLE',
    name: 'データ移行（開発・テスト）（データ移行プログラム・テスト）',
    assigneeUserUuid: 'demo-user-5',
    startDay: 183,
    endDay: 277,
    estimatedHour: 480,
    depth: 1,
  },
  {
    code: '5V5DYPBW',
    type: 'TASK',
    name: '移行プログラム開発',
    startDay: 183,
    endDay: 214,
    estimatedHour: 160,
    depth: 2,
  },
  {
    code: 'IJNDBFIR',
    type: 'TASK',
    name: '移行テスト実施',
    startDay: 214,
    endDay: 245,
    estimatedHour: 160,
    depth: 2,
  },
  {
    code: 'N2HERDQJ',
    type: 'TASK',
    name: '移行結果検証',
    startDay: 246,
    endDay: 277,
    estimatedHour: 160,
    depth: 2,
  },
  {
    code: 'BAQY4B79',
    type: 'DELIVERABLE',
    name: '移行リハーサル 第1回（移行リハーサル 第1回 実施）',
    assigneeUserUuid: 'demo-user-2',
    startDay: 241,
    endDay: 277,
    estimatedHour: 192,
    depth: 1,
  },
  {
    code: 'CWD8D7GO',
    type: 'TASK',
    name: 'リハーサル計画・準備',
    startDay: 241,
    endDay: 252,
    estimatedHour: 64,
    depth: 2,
  },
  {
    code: 'UDCYPQPB',
    type: 'TASK',
    name: 'リハーサル実施',
    startDay: 253,
    endDay: 264,
    estimatedHour: 64,
    depth: 2,
  },
  {
    code: 'SDE75QLR',
    type: 'TASK',
    name: '結果検証・課題対応',
    startDay: 265,
    endDay: 277,
    estimatedHour: 64,
    depth: 2,
  },
  {
    code: '57H5EFIS',
    type: 'DELIVERABLE',
    name: '移行リハーサル 第2回（移行リハーサル 第2回 実施）',
    assigneeUserUuid: 'demo-user-3',
    startDay: 278,
    endDay: 315,
    estimatedHour: 192,
    depth: 1,
  },
  {
    code: 'DRO3W11H',
    type: 'TASK',
    name: 'リハーサル計画・準備',
    startDay: 278,
    endDay: 289,
    estimatedHour: 64,
    depth: 2,
  },
  {
    code: 'Y7PCMVGR',
    type: 'TASK',
    name: 'リハーサル実施',
    startDay: 290,
    endDay: 303,
    estimatedHour: 64,
    depth: 2,
  },
  {
    code: '4HOMD5MK',
    type: 'TASK',
    name: '結果検証・課題対応',
    startDay: 303,
    endDay: 315,
    estimatedHour: 64,
    depth: 2,
  },

  {
    code: '9PJPRYBS',
    type: 'PROCESS',
    name: '運用準備',
    startDay: 252,
    endDay: 378,
    estimatedHour: 784,
    depth: 0,
  },
  {
    code: 'AG6LWNJV',
    type: 'DELIVERABLE',
    name: '運用準備（システム運用手順書）',
    assigneeUserUuid: demoUser.uuid,
    startDay: 252,
    endDay: 279,
    estimatedHour: 176,
    depth: 1,
  },
  {
    code: '3I88P25Y',
    type: 'TASK',
    name: '初版作成',
    startDay: 252,
    endDay: 260,
    estimatedHour: 56,
    depth: 2,
  },
  {
    code: '6A593O4C',
    type: 'TASK',
    name: '内部レビュー',
    startDay: 261,
    endDay: 270,
    estimatedHour: 64,
    depth: 2,
  },
  {
    code: '9XVXO884',
    type: 'TASK',
    name: 'レビュー結果反映',
    startDay: 270,
    endDay: 279,
    estimatedHour: 56,
    depth: 2,
  },
  {
    code: 'M81GDSZJ',
    type: 'DELIVERABLE',
    name: '運用準備（エンドユーザー向けマニュアル）',
    assigneeUserUuid: 'demo-user-5',
    startDay: 279,
    endDay: 307,
    estimatedHour: 168,
    depth: 1,
  },
  {
    code: 'N4XRCOIP',
    type: 'TASK',
    name: '初版作成',
    startDay: 279,
    endDay: 288,
    estimatedHour: 56,
    depth: 2,
  },
  {
    code: 'PAEGY66E',
    type: 'TASK',
    name: '内部レビュー',
    startDay: 289,
    endDay: 298,
    estimatedHour: 56,
    depth: 2,
  },
  {
    code: 'F9CFE9IM',
    type: 'TASK',
    name: 'レビュー結果反映',
    startDay: 298,
    endDay: 307,
    estimatedHour: 56,
    depth: 2,
  },
  {
    code: '9NR09GT4',
    type: 'DELIVERABLE',
    name: '運用準備（管理者向けマニュアル）',
    assigneeUserUuid: 'demo-user-2',
    startDay: 307,
    endDay: 336,
    estimatedHour: 184,
    depth: 1,
  },
  {
    code: '8SZQBQHS',
    type: 'TASK',
    name: '初版作成',
    startDay: 307,
    endDay: 316,
    estimatedHour: 64,
    depth: 2,
  },
  {
    code: '8WSO7XP5',
    type: 'TASK',
    name: '内部レビュー',
    startDay: 317,
    endDay: 325,
    estimatedHour: 56,
    depth: 2,
  },
  {
    code: 'T5ZY45QP',
    type: 'TASK',
    name: 'レビュー結果反映',
    startDay: 326,
    endDay: 336,
    estimatedHour: 64,
    depth: 2,
  },
  {
    code: '6CFUDOPV',
    type: 'DELIVERABLE',
    name: '運用テスト（運用テスト実施）',
    assigneeUserUuid: 'demo-user-3',
    startDay: 336,
    endDay: 378,
    estimatedHour: 256,
    depth: 1,
  },
  {
    code: '2ZH9J2W0',
    type: 'TASK',
    name: 'テスト実施',
    startDay: 336,
    endDay: 350,
    estimatedHour: 80,
    depth: 2,
  },
  {
    code: 'G7BIJQG8',
    type: 'TASK',
    name: '結果整理・障害登録',
    startDay: 350,
    endDay: 364,
    estimatedHour: 96,
    depth: 2,
  },
  {
    code: 'OIPJ0KT0',
    type: 'TASK',
    name: '障害修正確認',
    startDay: 365,
    endDay: 378,
    estimatedHour: 80,
    depth: 2,
  },

  {
    code: 'HSE4EFOL',
    type: 'PROCESS',
    name: 'ユーザートレーニング',
    startDay: 294,
    endDay: 371,
    estimatedHour: 480,
    depth: 0,
  },
  {
    code: 'FXTXN2JY',
    type: 'DELIVERABLE',
    name: 'トレーニング実施',
    assigneeUserUuid: demoUser.uuid,
    startDay: 294,
    endDay: 371,
    estimatedHour: 480,
    depth: 1,
  },
  {
    code: 'NQLABIAO',
    type: 'TASK',
    name: '教材作成',
    startDay: 294,
    endDay: 319,
    estimatedHour: 160,
    depth: 2,
  },
  {
    code: 'TO1NEJ6V',
    type: 'TASK',
    name: 'トレーニング実施',
    startDay: 320,
    endDay: 344,
    estimatedHour: 160,
    depth: 2,
  },
  {
    code: 'G2FK5AML',
    type: 'TASK',
    name: 'アンケート・フォローアップ',
    startDay: 345,
    endDay: 371,
    estimatedHour: 160,
    depth: 2,
  },

  {
    code: 'PV4AI5NS',
    type: 'PROCESS',
    name: 'リリース・移行',
    startDay: 364,
    endDay: 392,
    estimatedHour: 104,
    depth: 0,
  },
  {
    code: '1ORQZ1V8',
    type: 'DELIVERABLE',
    name: 'リリース作業',
    assigneeUserUuid: 'demo-user-5',
    startDay: 364,
    endDay: 392,
    estimatedHour: 104,
    depth: 1,
  },
  {
    code: 'I8KBBEZ8',
    type: 'TASK',
    name: 'リリース計画確認',
    startDay: 364,
    endDay: 374,
    estimatedHour: 40,
    depth: 2,
  },
  {
    code: '5SP26AKM',
    type: 'TASK',
    name: '本番リリース作業',
    startDay: 374,
    endDay: 383,
    estimatedHour: 24,
    depth: 2,
  },
  {
    code: 'DFS1KUIO',
    type: 'TASK',
    name: 'リリース後動作確認',
    startDay: 383,
    endDay: 392,
    estimatedHour: 40,
    depth: 2,
  },

  {
    code: 'MFU6L1JD',
    type: 'PROCESS',
    name: 'AI機能構築',
    startDay: 112,
    endDay: 392,
    estimatedHour: 1832,
    depth: 0,
  },
  {
    code: 'GZ66W5S5',
    type: 'DELIVERABLE',
    name: 'AI：学習データ収集・準備（A社）（学習データ収集・準備）',
    assigneeUserUuid: 'demo-user-2',
    startDay: 112,
    endDay: 161,
    estimatedHour: 248,
    depth: 1,
  },
  {
    code: '1TTKYOL3',
    type: 'TASK',
    name: 'データ要件定義',
    startDay: 112,
    endDay: 128,
    estimatedHour: 32,
    depth: 2,
  },
  {
    code: 'DVUHJ7WU',
    type: 'TASK',
    name: 'データ収集',
    startDay: 128,
    endDay: 144,
    estimatedHour: 96,
    depth: 2,
  },
  {
    code: 'PGDSJ071',
    type: 'TASK',
    name: 'データクレンジング・アノテーション',
    startDay: 145,
    endDay: 161,
    estimatedHour: 120,
    depth: 2,
  },
  {
    code: 'WANWAXXH',
    type: 'DELIVERABLE',
    name: 'AI：事前学習（事前学習実施）',
    assigneeUserUuid: 'demo-user-3',
    startDay: 128,
    endDay: 178,
    estimatedHour: 312,
    depth: 1,
  },
  {
    code: 'Z55EWT6T',
    type: 'TASK',
    name: '学習環境構築',
    startDay: 128,
    endDay: 144,
    estimatedHour: 96,
    depth: 2,
  },
  {
    code: 'PEWKPJX6',
    type: 'TASK',
    name: '事前学習実施',
    startDay: 145,
    endDay: 161,
    estimatedHour: 120,
    depth: 2,
  },
  {
    code: 'N3EBEATZ',
    type: 'TASK',
    name: '学習結果評価',
    startDay: 161,
    endDay: 178,
    estimatedHour: 96,
    depth: 2,
  },
  {
    code: 'RP93GTKR',
    type: 'DELIVERABLE',
    name: 'AI：精度検証・チューニング（精度検証・チューニング）',
    assigneeUserUuid: demoUser.uuid,
    startDay: 161,
    endDay: 260,
    estimatedHour: 480,
    depth: 1,
  },
  {
    code: 'CDYUBACH',
    type: 'TASK',
    name: '精度検証',
    startDay: 161,
    endDay: 194,
    estimatedHour: 160,
    depth: 2,
  },
  {
    code: '90ACP5C4',
    type: 'TASK',
    name: 'ハイパーパラメータチューニング',
    startDay: 194,
    endDay: 226,
    estimatedHour: 160,
    depth: 2,
  },
  {
    code: 'H6TOGMSO',
    type: 'TASK',
    name: '再検証',
    startDay: 227,
    endDay: 260,
    estimatedHour: 160,
    depth: 2,
  },
  {
    code: 'QHMG2X2C',
    type: 'DELIVERABLE',
    name: 'AI：デプロイ→ST組込（デプロイ・ST組込）',
    assigneeUserUuid: 'demo-user-5',
    startDay: 261,
    endDay: 309,
    estimatedHour: 312,
    depth: 1,
  },
  {
    code: 'JPE9KBU6',
    type: 'TASK',
    name: 'モデルデプロイ',
    startDay: 261,
    endDay: 276,
    estimatedHour: 96,
    depth: 2,
  },
  {
    code: 'SIAJA7S3',
    type: 'TASK',
    name: 'ST環境組込',
    startDay: 276,
    endDay: 292,
    estimatedHour: 96,
    depth: 2,
  },
  {
    code: '8TXP4NK1',
    type: 'TASK',
    name: '組込後動作確認',
    startDay: 293,
    endDay: 309,
    estimatedHour: 120,
    depth: 2,
  },
  {
    code: '493ECMIJ',
    type: 'DELIVERABLE',
    name: '精度向上対応',
    assigneeUserUuid: 'demo-user-2',
    startDay: 310,
    endDay: 392,
    estimatedHour: 480,
    depth: 1,
  },
  {
    code: 'X3QV2B45',
    type: 'TASK',
    name: '本番データ収集・分析',
    startDay: 310,
    endDay: 336,
    estimatedHour: 160,
    depth: 2,
  },
  {
    code: 'PEIN6P6R',
    type: 'TASK',
    name: '追加学習・チューニング',
    startDay: 338,
    endDay: 364,
    estimatedHour: 160,
    depth: 2,
  },
  {
    code: '56U5JBID',
    type: 'TASK',
    name: '精度評価・レポート',
    startDay: 364,
    endDay: 392,
    estimatedHour: 160,
    depth: 2,
  },

  {
    code: 'I2UF1L75',
    type: 'PROCESS',
    name: 'アプケーション運用',
    startDay: 392,
    endDay: 434,
    estimatedHour: 192,
    depth: 0,
  },
  {
    code: 'HEIFL58T',
    type: 'DELIVERABLE',
    name: '運用対応',
    assigneeUserUuid: 'demo-user-3',
    startDay: 392,
    endDay: 434,
    estimatedHour: 192,
    depth: 1,
  },
  {
    code: '44PXINW5',
    type: 'TASK',
    name: '運用監視',
    startDay: 392,
    endDay: 405,
    estimatedHour: 64,
    depth: 2,
  },
  {
    code: '0MB7NIXA',
    type: 'TASK',
    name: '問い合わせ対応',
    startDay: 406,
    endDay: 419,
    estimatedHour: 64,
    depth: 2,
  },
  {
    code: 'N03WGPUI',
    type: 'TASK',
    name: '定期メンテナンス',
    startDay: 419,
    endDay: 434,
    estimatedHour: 64,
    depth: 2,
  },
]

// タスクのステータスは予定日とTODAY_DAY_OFFSET（shared.ts）の位置関係から
// 決まる。予定日は今日基準で動くため、ステータスも同じ基準で導出することで
// いつデモを開いても日付と噛み合う。
//   予定終了日 < 今日   → 完了済み（DONE）
//   予定期間が今日を含む → 対応中（DOING）またはレビュー中（REVIEW）
//   予定開始日 > 今日   → 未着手（TODO）
// 今日をまたぐタスクのうち、予定期間の後半に入っているものはレビュー中、
// 前半のものは対応中とする（レビュー工程だけが常にREVIEWになるより、
// 進捗に応じて散る方が実際のプロジェクトに近い）。
const isInReview = (startDay: number, endDay: number) => {
  const elapsed = DEMO_TODAY_DAY_OFFSET - startDay
  const duration = endDay - startDay
  return duration > 0 && elapsed / duration >= 0.5
}

// 破棄（DISCARD）だけは日付から導出できないため、対象をコードで指定する。
// 予定日が過去の項目を選び、「着手したが途中で破棄した」実績付きの
// 破棄行として表示する。
const DISCARDED_TASK_CODES = new Set([
  '53YOMZTW', // 顧客レビュー配下のインターフェース一覧: 成果物スコープ変更により破棄
])

// 遅延（予定終了日を過ぎても未完了）のタスク。破棄と同じく日付からは導出
// できないため、対象をコードで指定する。
//
// 日付だけでステータスを決めると「予定終了日 < 今日 → 完了」となり、
// 遅延がひとつも存在しないプロジェクトになってしまう。それでは進捗レポートの
// 遅延列・天気（曇/雨）・遅延コメントが常に空振りし、WBSやガントでも
// 「計画どおり」しか見せられない。実際のプロジェクトでは一部が遅れるのが
// 普通なので、代表的な遅延をデータとして持たせる。
//
// 対象は「データ一覧」「画面一覧」配下に寄せ、担当者もdemo-user-5に揃える。
// 特定の成果物・特定の担当者に遅れが集中している状況を作ることで、
// 「どこで詰まっているか」「誰に負荷が寄っているか」がレポートから
// 読み取れるようにする狙い（進捗レポートのコメントはこの偏りを見て
// 「他メンバーへの再配分」を提案する）。
//
// 予定終了日が異なるものを選んでいるのは、週次レポートの世代が進むほど
// 遅延が増える（第1回1件 → 第2回2件 → 第3回3件）ようにするため。
// 過去の回を開くとその時点の遅延件数になり、週を追うごとに悪化している
// 様子が世代間比較で見える。
const DELAYED_TASK_CODES = new Set([
  'CGJ0EWES', // データ一覧の項目整理・一覧化: 対象システムの洗い出しが難航し停滞
  '3FYON1S4', // データ一覧のレビュー: 上記の遅れを受けてレビュー着手が後ろ倒し
  'TY63246W', // 画面一覧のEC在庫管理領域: 仕様確定待ちで未完了
])

const resolveTaskStatus = (item: {
  code: string
  startDay: number
  endDay: number
}): DemoWbsItemStatus => {
  if (DISCARDED_TASK_CODES.has(item.code)) return 'DISCARD'
  const { startDay, endDay } = item
  if (startDay > DEMO_TODAY_DAY_OFFSET) return 'TODO'
  // 遅延タスクは予定終了日を過ぎていても完了させない。着手はしているので
  // 対応中（DOING）とし、実績工数も積まれる状態にする。
  if (DELAYED_TASK_CODES.has(item.code)) return 'DOING'
  if (endDay < DEMO_TODAY_DAY_OFFSET) return 'DONE'
  return isInReview(startDay, endDay) ? 'REVIEW' : 'DOING'
}

// 着手前のタスクには実績（投下工数）が付かない。未着手(TODO)に加え、
// 予定開始日が未来の破棄(DISCARD)も着手前として扱う。
// 実績時間(resolveActualHour)と実績明細(buildTaskActualWorks)は
// 必ずこの判定を共有し、「実績時間はあるのに明細が0件」を防ぐ。
const isNotStartedYet = (status: DemoWbsItemStatus, startDate: string) =>
  status === 'TODO' || startDate > dayOffsetToDate(DEMO_TODAY_DAY_OFFSET)

// 実績時間は、完了・破棄は見積どおり、対応中・レビュー中は途中なので
// 見積の一部のみ消化済みとする（進捗率が100%に見えないようにする）。
const IN_PROGRESS_ACTUAL_HOUR_RATIO = 0.6

// 工数は人日表示（1日=HOURS_PER_WORKDAY時間）に換算されるため、端数時間が
// あると「0.6人日」のような値が並んでしまう。デモデータでは稼働日単位に
// 丸めて、時間・人日のどちらでも整数で見えるようにする。
const roundToWorkdayUnit = (hour: number): number =>
  Math.round(hour / HOURS_PER_WORKDAY) * HOURS_PER_WORKDAY

const resolveActualHour = (
  status: DemoWbsItemStatus,
  estimatedHour: number,
  startDate: string
): number => {
  if (isNotStartedYet(status, startDate)) return 0
  if (status === 'DONE' || status === 'DISCARD') return estimatedHour
  // 着手済みの実績が0になると明細も作られず「対応中なのに実績なし」に
  // なるため、最低1稼働日は積む。
  return Math.max(
    HOURS_PER_WORKDAY,
    roundToWorkdayUnit(estimatedHour * IN_PROGRESS_ACTUAL_HOUR_RATIO)
  )
}

const demoTypeDtoByRootType: {
  [type in
    | 'PROCESS'
    | 'DELIVERABLE_LIST'
    | 'DELIVERABLE'
    | 'TASK']: typeof demoWbsItemTypeWorkgroup
} = {
  PROCESS: demoWbsItemTypeProcess,
  DELIVERABLE_LIST: demoWbsItemTypeDeliverableList,
  DELIVERABLE: demoWbsItemTypeDeliverable,
  TASK: demoWbsItemType,
}

// 自己レビューを避けるための代替責任者。責任者は各チーム1名なので、
// 自分以外の責任者＝もう一方のチームの責任者になる。
const otherTeamAccountableUserUuid = (accountableUserUuid: string) =>
  DEMO_ACCOUNTABLE_USER_UUIDS.find(
    userUuid => userUuid !== accountableUserUuid
  ) ?? DEMO_PM_USER_UUID

// WBS項目の責任者・担当者・チームは成果物単位で決まる。
// 成果物とその配下タスクは同じ担当者・同じチームが持つ。
type DemoItemMembers = {
  accountable: DemoMemberRef
  responsible: DemoMemberRef
  assignee: DemoMemberRef
  team: (typeof demoTeams)[number]
}

// 成果物の担当者（REQUIREMENTS_PHASE_ITEMSのassigneeUserUuid）から体制を組む。
// 担当者を規則で機械的に割り振ると、デモで見せたい期間に誰のカードが並ぶかを
// データ側から指定できない（カンバンの初期表示は予定終了日で絞り込むため、
// 規則次第で特定の担当者のスイムレーンが数枚だけになる）。担当者は各成果物が
// 直接持ち、ここでは残りを導出するだけにする。
//   チーム: 担当者の所属（members.tsのDEMO_TEAM_UUID_BY_USER_UUID）
//   責任者: そのチームの責任者。ただし担当者が責任者本人の場合は
//           自己レビューを避けてもう一方のチームの責任者が持つ
//           （責任者はプロジェクト横断で見る立場のため）
//   responsible（取りまとめ担当）: assigneeと同一人物
//
// 担当者が未指定・不明の場合はPMを担当にする。membersByCodeはモジュール評価時
// に構築されるため、ここで例外を投げるとimport元（デモ画面）ごと落ちてしまう。
const resolveItemMembers = (
  assigneeUserUuid: string | undefined
): DemoItemMembers => {
  const resolvedUserUuid =
    assigneeUserUuid && demoProjectMemberRefByUserUuid(assigneeUserUuid)
      ? assigneeUserUuid
      : DEMO_PM_USER_UUID
  const assignee = requireDemoProjectMemberRef(resolvedUserUuid)
  const team = demoTeamByUserUuid(resolvedUserUuid)
  const teamAccountableUserUuid =
    DEMO_TEAM_ROSTERS.find(roster => roster.team.uuid === team.uuid)
      ?.accountableUserUuid ?? DEMO_PM_USER_UUID
  const accountableUserUuid =
    resolvedUserUuid === teamAccountableUserUuid
      ? otherTeamAccountableUserUuid(teamAccountableUserUuid)
      : teamAccountableUserUuid
  return {
    accountable: requireDemoProjectMemberRef(accountableUserUuid),
    responsible: assignee,
    assignee,
    team,
  }
}

// ワークグループ行とプロジェクトマネジメント工程の体制。どちらも
// プロジェクト全体を見るPMが責任者・担当者を兼ねる
// （members.tsのDEMO_PM_USER_UUID参照）。
// 他の工程は各チームの責任者に散らすため resolveProcessMembers を使う。
const demoRootRowMembers: DemoItemMembers = {
  accountable: requireDemoProjectMemberRef(DEMO_PM_USER_UUID),
  responsible: requireDemoProjectMemberRef(DEMO_PM_USER_UUID),
  assignee: requireDemoProjectMemberRef(DEMO_PM_USER_UUID),
  team: demoTeamByUserUuid(DEMO_PM_USER_UUID),
}

// 工程（depth 0）の体制。初期表示は工程の行までしか出ないため、ここを全て
// PMにすると画面に見える担当者・責任者が1人だけになってしまう。
// 各チームの責任者が交互に担当し、レビュー側はもう一方の責任者が持つ。
const resolveProcessMembers = (processIndex: number): DemoItemMembers => {
  const assigneeUserUuid =
    DEMO_ACCOUNTABLE_USER_UUIDS[
      processIndex % DEMO_ACCOUNTABLE_USER_UUIDS.length
    ]
  const assignee = requireDemoProjectMemberRef(assigneeUserUuid)
  return {
    accountable: requireDemoProjectMemberRef(
      otherTeamAccountableUserUuid(assigneeUserUuid)
    ),
    responsible: assignee,
    assignee,
    team: demoTeamByUserUuid(assigneeUserUuid),
  }
}

const membersByCode: { [code: string]: DemoItemMembers } = {}
{
  let processIndex = 0
  let currentMembers: DemoItemMembers | undefined
  const deliverableListCodesAwaitingMembers: string[] = []
  REQUIREMENTS_PHASE_ITEMS.forEach(item => {
    if (item.depth <= 0) {
      membersByCode[item.code] = resolveProcessMembers(processIndex)
      processIndex++
      currentMembers = undefined
      deliverableListCodesAwaitingMembers.length = 0
      return
    }
    // 成果物一覧は取りまとめ役なので独自の体制を持たず、直下の成果物と
    // 揃える。ここで割り当てを進めてしまうと、一覧とその配下で担当者・
    // チームが食い違って見える。
    if (item.type === 'DELIVERABLE_LIST') {
      deliverableListCodesAwaitingMembers.push(item.code)
      return
    }
    if (item.type !== 'TASK') {
      const members = resolveItemMembers(item.assigneeUserUuid)
      currentMembers = members
      // 直前に現れた成果物一覧（入れ子もありうる）へ、最初の成果物の
      // 体制をまとめて引き継ぐ。
      deliverableListCodesAwaitingMembers.forEach(code => {
        membersByCode[code] = members
      })
      deliverableListCodesAwaitingMembers.length = 0
    }
    // TASKはここに来た時点でcurrentMembers（直近の成果物）を引き継ぐ。
    if (currentMembers) membersByCode[item.code] = currentMembers
  })
}

// 終了状態。この2つだけで構成される親（成果物・工程）は完了にできる。
const CLOSED_TASK_STATUSES = new Set<DemoWbsItemStatus>(['DONE', 'DISCARD'])

// 成果物・工程・チケット管理表（＝配下を持つ項目）のステータスは、配下の
// 状態から決まる。
//   配下が全て終了状態(DONE/DISCARD) → 完了（DONE）
//   配下が全て未着手(TODO)           → 未着手（TODO）
//   上記以外                         → 対応中（DOING）
// 3つ目には次が含まれる。
//   ・DOINGやREVIEWの配下が1つでもある（着手済みなのでDOING。親にREVIEWは使わない）
//   ・完了済みと未着手の混在（一部でも終わっているものを未着手とは呼べない）
// 初期データ生成と実行時の再計算（recalcAncestorStatuses）が同じ規則を
// 使うよう、判定はここに1本化する。
export const resolveStatusFromChildren = (
  childStatuses: DemoWbsItemStatus[]
): DemoWbsItemStatus => {
  if (childStatuses.every(status => CLOSED_TASK_STATUSES.has(status))) {
    return 'DONE'
  }
  if (childStatuses.every(status => status === 'TODO')) return 'TODO'
  return 'DOING'
}

// 成果物・工程のステータスは配下タスクから導出する（resolveStatusFromChildren）。
// REQUIREMENTS_PHASE_ITEMSはdepthを持つフラット配列なので、深い項目から順に
// 「自分の直下の子」の状態を積み上げることで、孫の状態も親→祖先へ伝播する。
const nonTaskStatusByCode: { [code: string]: DemoWbsItemStatus } = {}
{
  // 各項目の直下の子のステータス。深い項目から処理するため、子の集計が
  // 済んだ時点で親を評価できる。
  const childStatusesByCode: { [code: string]: DemoWbsItemStatus[] } = {}

  // 自分より1つ浅い階層で直近に現れた項目が親。深さごとに最後の項目を
  // 保持しながら1回走査する。
  const parentCodeByCode: { [code: string]: string } = {}
  const latestCodeByDepth: { [depth: number]: string } = {}
  REQUIREMENTS_PHASE_ITEMS.forEach(item => {
    const parentCode = latestCodeByDepth[item.depth - 1]
    if (parentCode) parentCodeByCode[item.code] = parentCode
    latestCodeByDepth[item.depth] = item.code
  })

  // 深い項目から順に、自分のステータスを確定して親へ積む。
  ;[...REQUIREMENTS_PHASE_ITEMS]
    .sort((a, b) => b.depth - a.depth)
    .forEach(item => {
      const status =
        item.type === 'TASK'
          ? resolveTaskStatus(item)
          : resolveStatusFromChildren(childStatusesByCode[item.code] ?? [])
      if (item.type !== 'TASK') nonTaskStatusByCode[item.code] = status
      const parentCode = parentCodeByCode[item.code]
      if (parentCode) (childStatusesByCode[parentCode] ??= []).push(status)
    })
}

// ルート行（ワークグループ）が表示する計画全体の期間・工数。工程の追加や
// 予定日の変更に追従させるため、固定値ではなく配下の工程（depth 0）から
// 集計する。見積り・実績とも、配下の合計＝親の値という他の階層と同じ規則。
const DEMO_ROOT_PROCESS_ITEMS = REQUIREMENTS_PHASE_ITEMS.filter(
  item => item.depth === 0
)

export const DEMO_PLAN_START_DAY = Math.min(
  ...DEMO_ROOT_PROCESS_ITEMS.map(item => item.startDay)
)

export const DEMO_PLAN_END_DAY = Math.max(
  ...DEMO_ROOT_PROCESS_ITEMS.map(item => item.endDay)
)

const DEMO_PLAN_ESTIMATED_HOUR = DEMO_ROOT_PROCESS_ITEMS.reduce(
  (total, item) => total + item.estimatedHour,
  0
)

// 実績は各工程のステータス・予定日から決まる値（resolveActualHour）の合計。
// 個々の行に表示される実績と、ルート行の実績が食い違わないようにする。
const DEMO_PLAN_ACTUAL_HOUR = DEMO_ROOT_PROCESS_ITEMS.reduce(
  (total, item) =>
    total +
    resolveActualHour(
      nonTaskStatusByCode[item.code] ?? 'DONE',
      item.estimatedHour,
      dayOffsetToDate(item.startDay)
    ),
  0
)

// チケットリスト（DELIVERABLE、ticketType='ISSUE_LIST'|'BUG_LIST'|...）と、
// その配下のチケット（TASK、ticketType='ISSUE'|'BUG'|...）をワークグループ
// 直下に追加する。addWbsItemは通常のWBSツリーノード生成をそのまま流用でき、
// ticketTypeを渡すだけでticket_lists/ticketsハンドラが参照するdemoWbsItemBodies
// エントリになる（toWbsItemBasicが読むticketType/ticketListUuidは呼び出し側で
// 個別に上書きする）。
const addTicketList = (overrides: {
  uuid: string
  code: string
  displayName: string
  typeDto: ReturnType<typeof buildTicketTypeList>
  ticketType: string
  status: DemoWbsItemStatus
  startDay: number
  endDay: number
  accountable: DemoMemberRef
  responsible: DemoMemberRef
  assignee: DemoMemberRef
  team: (typeof demoTeams)[number]
}) => {
  const node = addWbsItem({
    uuid: overrides.uuid,
    code: overrides.code,
    type: 'DELIVERABLE',
    typeDto: overrides.typeDto,
    displayName: overrides.displayName,
    status: overrides.status,
    startDate: dayOffsetToDate(overrides.startDay),
    endDate: dayOffsetToDate(overrides.endDay),
    estimatedHour: 0,
    actualHour: 0,
    accountable: overrides.accountable,
    responsible: overrides.responsible,
    assignee: overrides.assignee,
    team: overrides.team,
  })
  demoWbsItemBodies[overrides.uuid].ticketType = overrides.ticketType
  return node
}

const addTicket = (overrides: {
  uuid: string
  code: string
  displayName: string
  typeDto: ReturnType<typeof buildTicketType>
  ticketType: string
  ticketListUuid: string
  status: DemoWbsItemStatus
  startDay: number
  endDay: number
  estimatedHour: number
  accountable: DemoMemberRef
  responsible: DemoMemberRef
  assignee: DemoMemberRef
  team: (typeof demoTeams)[number]
}) => {
  const node = addWbsItem({
    uuid: overrides.uuid,
    code: overrides.code,
    type: 'TASK',
    typeDto: overrides.typeDto,
    displayName: overrides.displayName,
    status: overrides.status,
    startDate: dayOffsetToDate(overrides.startDay),
    endDate: dayOffsetToDate(overrides.endDay),
    estimatedHour: overrides.estimatedHour,
    actualHour: resolveActualHour(
      overrides.status,
      overrides.estimatedHour,
      dayOffsetToDate(overrides.startDay)
    ),
    accountable: overrides.accountable,
    responsible: overrides.responsible,
    assignee: overrides.assignee,
    team: overrides.team,
  })
  demoWbsItemBodies[overrides.uuid].ticketType = overrides.ticketType
  demoWbsItemBodies[overrides.uuid].ticketListUuid = overrides.ticketListUuid
  return node
}

// 課題管理・不具合管理・リスク管理・変更管理・会議管理の5種類、それぞれに
// チケットリスト1件・チケット1件をデモデータとして用意する。
// ticketStatusは種別ごとに散らし、チケット一覧でもステータス別の表示を
// 確認できるようにする（チケットリスト自身は常に進行中）。
// TicketListDetail.ticketType（ticket_lists/listのticketTypeクエリで絞り込む
// 対象）は、チケットリスト自身の型コード（例: ISSUE_LIST）ではなく、
// 配下チケットの型コード（例: ISSUE）で持つのが実仕様（ProjectPlanNewの
// addTicketがTicketListAPI.getAll({ ticketType: ticketType.code })を
// stateProject.ticketTypes = ticketTypes.filter(v => !v.child)、つまり
// 子を持たない側＝チケット本体コードで呼ぶため）。listとticketで同じ
// ticketType値を共有する。
// チケット管理表を束ねる工程。実プロジェクトでも課題・不具合・リスク・変更・
// 会議の管理はプロジェクトマネジメント業務として一箇所にまとめられるため、
// 各管理表を工程「プロジェクトマネジメント」の配下に置く。
const DEMO_PROJECT_MANAGEMENT_PROCESS = {
  uuid: 'demo-process-project-management',
  code: 'DEMO-PS-PM',
  displayName: 'プロジェクトマネジメント',
} as const

// 期間は「今日」（shared.tsのTODAY_DAY_OFFSET = day 42）との整合を取る。
// 対応中・レビュー中のチケットは今日を含む期間、完了は今日より前に終わり、
// 未着手は今日より後に始まる。ガント上で進行中／完了／未着手のバーが
// 今日線の前後に自然に散らばり、計画として読める見え方になる。
// チケット管理表は種別ごとに開始時期をずらし（全て同じ全幅バーにならない）、
// 計画終盤まで継続する。
const DEMO_TICKET_SEEDS = [
  {
    key: 'issue',
    assigneeUserUuid: 'demo-user-2',
    listUuid: 'demo-ticket-list-issue-1',
    listCode: 'DEMO-TL-ISSUE',
    listDisplayName: '課題管理',
    listTypeDto: demoTicketTypeIssueList,
    ticketUuid: 'demo-ticket-issue-1',
    ticketCode: 'DEMO-TK-ISSUE-0001',
    ticketDisplayName: '要件定義レビューの指摘対応',
    listStartDay: 0,
    listEndDay: 392,
    ticketStartDay: 28,
    ticketEndDay: 49,
    ticketEstimatedHour: 16,
    ticketTypeDto: demoTicketTypeIssue,
    ticketType: 'ISSUE',
    ticketStatus: 'DOING',
  },
  {
    key: 'bug',
    assigneeUserUuid: 'demo-user-3',
    listUuid: 'demo-ticket-list-bug-1',
    listCode: 'DEMO-TL-BUG',
    listDisplayName: '不具合管理',
    listTypeDto: demoTicketTypeBugList,
    ticketUuid: 'demo-ticket-bug-1',
    ticketCode: 'DEMO-TK-BUG-0001',
    ticketDisplayName: 'ログイン画面の表示崩れ',
    listStartDay: 21,
    listEndDay: 392,
    ticketStartDay: 35,
    ticketEndDay: 56,
    ticketEstimatedHour: 24,
    ticketTypeDto: demoTicketTypeBug,
    ticketType: 'BUG',
    ticketStatus: 'REVIEW',
  },
  {
    key: 'risk',
    assigneeUserUuid: demoUser.uuid,
    listUuid: 'demo-ticket-list-risk-1',
    listCode: 'DEMO-TL-RISK',
    listDisplayName: 'リスク管理',
    listTypeDto: demoTicketTypeRiskList,
    ticketUuid: 'demo-ticket-risk-1',
    ticketCode: 'DEMO-TK-RISK-0001',
    ticketDisplayName: '外部連携仕様未確定によるスケジュール遅延リスク',
    listStartDay: 0,
    listEndDay: 392,
    ticketStartDay: 63,
    ticketEndDay: 119,
    ticketEstimatedHour: 40,
    ticketTypeDto: demoTicketTypeRisk,
    ticketType: 'RISK',
    ticketStatus: 'TODO',
  },
  {
    key: 'changeRequest',
    assigneeUserUuid: 'demo-user-5',
    listUuid: 'demo-ticket-list-change-request-1',
    listCode: 'DEMO-TL-CR',
    listDisplayName: '変更管理',
    listTypeDto: demoTicketTypeChangeRequestList,
    ticketUuid: 'demo-ticket-change-request-1',
    ticketCode: 'DEMO-TK-CR-0001',
    ticketDisplayName: '業務フロー範囲の追加変更',
    listStartDay: 0,
    listEndDay: 392,
    ticketStartDay: 7,
    ticketEndDay: 35,
    ticketEstimatedHour: 32,
    ticketTypeDto: demoTicketTypeChangeRequest,
    ticketType: 'CHANGE_REQUEST',
    ticketStatus: 'DONE',
  },
  {
    key: 'meeting',
    assigneeUserUuid: 'demo-user-2',
    listUuid: 'demo-ticket-list-meeting-1',
    listCode: 'DEMO-TL-MTG',
    listDisplayName: '会議管理',
    listTypeDto: demoTicketTypeMeetingList,
    ticketUuid: 'demo-ticket-meeting-1',
    ticketCode: 'DEMO-TK-MTG-0001',
    ticketDisplayName: '定例進捗会議',
    listStartDay: 0,
    listEndDay: 434,
    ticketStartDay: 0,
    ticketEndDay: 434,
    ticketEstimatedHour: 240,
    ticketTypeDto: demoTicketTypeMeeting,
    ticketType: 'MEETING',
    ticketStatus: 'DOING',
  },
] as const

export const DEMO_TICKET_LIST_UUIDS = Object.fromEntries(
  DEMO_TICKET_SEEDS.map(seed => [seed.key, seed.listUuid])
) as { [K in (typeof DEMO_TICKET_SEEDS)[number]['key']]: string }

// プロジェクトマネジメント工程の期間・工数。配下の合計＝親の値という
// 他の階層と同じ規則で、チケット管理表の期間・チケットの工数から集計する。
const DEMO_PROJECT_MANAGEMENT_START_DAY = Math.min(
  ...DEMO_TICKET_SEEDS.map(seed => seed.listStartDay)
)

const DEMO_PROJECT_MANAGEMENT_END_DAY = Math.max(
  ...DEMO_TICKET_SEEDS.map(seed => seed.listEndDay)
)

const DEMO_PROJECT_MANAGEMENT_ESTIMATED_HOUR = DEMO_TICKET_SEEDS.reduce(
  (total, seed) => total + seed.ticketEstimatedHour,
  0
)

const DEMO_PROJECT_MANAGEMENT_ACTUAL_HOUR = DEMO_TICKET_SEEDS.reduce(
  (total, seed) =>
    total +
    resolveActualHour(
      seed.ticketStatus,
      seed.ticketEstimatedHour,
      dayOffsetToDate(seed.ticketStartDay)
    ),
  0
)

export const buildDemoWbsTree = () => {
  // 以下の項目群を束ねるルート行。実物のWBS階層ではPROCESSの親はWORKGROUPだが、
  // ここではrootTypeをPROCESSにしたワークグループ見た目の行を使う
  // （理由はdemoWbsItemTypeWorkgroupRoot参照）。そのため親子関係としては
  // 「PROJECT直下のPROCESS」となり、canBeChildOfもそれに合わせている。
  const workgroupUuid = 'demo-wg-1'
  const workgroupNode = addWbsItem({
    uuid: workgroupUuid,
    code: 'DEMO-WG',
    type: 'PROCESS',
    typeDto: demoWbsItemTypeWorkgroupRoot,
    displayName: 'ワークグループ',
    status: 'DOING',
    startDate: dayOffsetToDate(DEMO_PLAN_START_DAY),
    endDate: dayOffsetToDate(DEMO_PLAN_END_DAY),
    // 配下はプロジェクトマネジメント工程＋各工程なので、両方を足す。
    estimatedHour:
      DEMO_PLAN_ESTIMATED_HOUR + DEMO_PROJECT_MANAGEMENT_ESTIMATED_HOUR,
    actualHour: DEMO_PLAN_ACTUAL_HOUR + DEMO_PROJECT_MANAGEMENT_ACTUAL_HOUR,
    ...demoRootRowMembers,
  })

  // プロジェクトマネジメント工程は要件定義より前（WBSの先頭）に置く。
  // 期間・工数は他の階層と同じく配下（チケット管理表）の集計値にする。
  const projectManagementNode = addWbsItem({
    uuid: DEMO_PROJECT_MANAGEMENT_PROCESS.uuid,
    code: DEMO_PROJECT_MANAGEMENT_PROCESS.code,
    type: 'PROCESS',
    typeDto: demoWbsItemTypeProcess,
    displayName: DEMO_PROJECT_MANAGEMENT_PROCESS.displayName,
    status: resolveStatusFromChildren(
      DEMO_TICKET_SEEDS.map(seed =>
        resolveStatusFromChildren([seed.ticketStatus])
      )
    ),
    startDate: dayOffsetToDate(DEMO_PROJECT_MANAGEMENT_START_DAY),
    endDate: dayOffsetToDate(DEMO_PROJECT_MANAGEMENT_END_DAY),
    estimatedHour: DEMO_PROJECT_MANAGEMENT_ESTIMATED_HOUR,
    actualHour: DEMO_PROJECT_MANAGEMENT_ACTUAL_HOUR,
    ...demoRootRowMembers,
  })
  workgroupNode.children.push(projectManagementNode)

  // parentStack[depth]はその深さで直近に作成したノードを保持し、
  // 各項目は自分より1つ浅い深さの最新ノードの下にネストされる。
  const parentStack: any[] = [workgroupNode]

  REQUIREMENTS_PHASE_ITEMS.forEach(item => {
    const status =
      item.type === 'TASK'
        ? resolveTaskStatus(item)
        : (nonTaskStatusByCode[item.code] ?? 'DONE')
    const node = addWbsItem({
      uuid: item.code,
      code: item.code,
      type: item.type,
      typeDto: demoTypeDtoByRootType[item.type],
      displayName: item.name,
      status,
      startDate: dayOffsetToDate(item.startDay),
      endDate: dayOffsetToDate(item.endDay),
      estimatedHour: item.estimatedHour,
      actualHour: resolveActualHour(
        status,
        item.estimatedHour,
        dayOffsetToDate(item.startDay)
      ),
      ...membersByCode[item.code],
    })

    // parentStack[0]はworkgroupNode自身なので、項目の親（1つ浅い階層）は
    // depth - 1ではなくparentStack[item.depth]にある。
    const parent = parentStack[item.depth]
    parent.children.push(node)
    parentStack[item.depth + 1] = node
  })

  DEMO_TICKET_SEEDS.forEach(seed => {
    // チケットもWBS項目と同じく、担当者をシード側で持たせて体制を組む。
    const members = resolveItemMembers(seed.assigneeUserUuid)
    const listNode = addTicketList({
      uuid: seed.listUuid,
      code: seed.listCode,
      displayName: seed.listDisplayName,
      typeDto: seed.listTypeDto,
      ticketType: seed.ticketType,
      // チケット管理表も配下チケットからステータスを導出する
      // （成果物・工程と同じ扱い）。
      status: resolveStatusFromChildren([seed.ticketStatus]),
      startDay: seed.listStartDay,
      endDay: seed.listEndDay,
      ...members,
    })
    const ticketNode = addTicket({
      uuid: seed.ticketUuid,
      code: seed.ticketCode,
      displayName: seed.ticketDisplayName,
      typeDto: seed.ticketTypeDto,
      ticketType: seed.ticketType,
      ticketListUuid: seed.listUuid,
      status: seed.ticketStatus,
      startDay: seed.ticketStartDay,
      endDay: seed.ticketEndDay,
      estimatedHour: seed.ticketEstimatedHour,
      ...members,
    })
    listNode.children.push(ticketNode)
    projectManagementNode.children.push(listNode)
  })

  return [workgroupNode]
}

// GET /api/v1/projects/plans/skeleton はトップレベルの配列ではなく、
// 単一のルートProjectPlanSkeletonノード（プロジェクト自身）を返す。
// UIはsource.childrenを読んでツリーを構築する。
export const demoWbsItemSkeletonRoot = {
  uuid: demoProject.uuid,
  lockVersion: 1,
  type: 'PROJECT',
  wbsItemUuid: demoProject.uuid,
  children: buildDemoWbsTree(),
}

// WBS項目のuuid -> 親スケルトンノードのマップ。plans/path・path_wbsハンドラ
// が、パンくずに必要な祖先チェーンを組み立てるのに使う。ルート行（ワーク
// グループ）自身はこのマップに載らないため、祖先チェーンはその直下から始まる。
//
// 初期化時に1回だけ作るスナップショットにはできない。行の追加・移動・削除
// （insertNodeIntoTree/removeNodeFromTree）はツリーのchildrenを直接
// 書き換えるため、事前計算したマップは即座に古くなり、実行時に追加された
// 行のパンくずが空になってしまう。呼ばれた時点のツリーから毎回導出する。
export const getDemoWbsItemParentNodeByUuid = () => {
  const parentNodeByUuid: { [uuid: string]: any } = {}
  const visit = (node: any) => {
    ;(node.children ?? []).forEach((child: any) => {
      parentNodeByUuid[child.wbsItemUuid] = node
      visit(child)
    })
  }
  demoWbsItemSkeletonRoot.children.forEach(visit)
  return parentNodeByUuid
}

// 配下のステータス変更を祖先へ反映する。本番はバックエンドが保存時に
// 行う処理なので、モックでも更新系ハンドラから呼んで同じ見え方にする。
//
// 対象は配下を持つ全ての項目（成果物・工程・ワークグループ・チケット管理表）。
// 判定規則はresolveStatusFromChildrenに従い、初期データと一致させる。
// 手動で設定した親のステータスも再計算で上書きする（常に配下の状態を映す）。
// 例外は破棄済み(DISCARD)の項目で、配下が動いても復活させない。
// ステータスが変わった項目は実績日も新しいステータスに合わせて振り直す。
export const recalcAncestorStatuses = (
  changedWbsItemUuids: string[],
  updatedBy: unknown
): void => {
  const parentNodeByUuid = getDemoWbsItemParentNodeByUuid()

  // 再計算対象＝変更された項目自身（配下を持つ場合）とその全祖先。
  // 自身を含めるのは、行の削除で「親自身」を起点に渡す呼び出し方
  // （削除された行はもうツリーに無いため）に対応するため。
  // 配下を持たないTASKは対象外（配下から導出できない）。
  const targetUuids = new Set<string>()
  changedWbsItemUuids.forEach(changedUuid => {
    if (demoWbsItemSkeletonNodesByUuid[changedUuid]?.children?.length) {
      targetUuids.add(changedUuid)
    }
    let parent = parentNodeByUuid[changedUuid]
    while (parent) {
      const parentUuid = parent.wbsItemUuid
      // 複数の子から同じ祖先に到達しうるので、既訪なら打ち切る。
      if (targetUuids.has(parentUuid)) break
      targetUuids.add(parentUuid)
      parent = parentNodeByUuid[parentUuid]
    }
  })

  // ルートからの深さ。子が必ず親より先に評価されるよう、これの降順で
  // 処理する（変更点からの距離で並べると、浅い経路で到達した親が自分の
  // 子より先に評価され、孫からの変更が祖先へ伝播しない）。
  const rootDistanceOf = (uuid: string): number => {
    let distance = 0
    let parent = parentNodeByUuid[uuid]
    while (parent) {
      distance++
      parent = parentNodeByUuid[parent.wbsItemUuid]
    }
    return distance
  }
  const rootDistanceByUuid: { [uuid: string]: number } = {}
  targetUuids.forEach(uuid => {
    rootDistanceByUuid[uuid] = rootDistanceOf(uuid)
  })
  ;[...targetUuids]
    .sort((a, b) => rootDistanceByUuid[b] - rootDistanceByUuid[a])
    .forEach(uuid => {
      const node = demoWbsItemSkeletonNodesByUuid[uuid]
      const currentBody = demoWbsItemBodies[uuid]
      if (!node || !currentBody) return
      const childStatuses = (node.children ?? [])
        .map(child => demoWbsItemBodies[child.wbsItemUuid]?.status)
        .filter((status): status is DemoWbsItemStatus => !!status)
      // 配下が無い項目（葉になった成果物など）は配下から導出できない。
      if (childStatuses.length === 0) return
      // 破棄済みの項目は配下の状態で復活させない（本体の
      // resolveStatusByActualDateがDISCARDを保護しているのと同じ扱い）。
      if (currentBody.status === 'DISCARD') return
      const nextStatus = resolveStatusFromChildren(childStatuses)
      if (nextStatus === currentBody.status) return
      currentBody.status = nextStatus
      currentBody.substatus = DEFAULT_SUBSTATUS_BY_STATUS[nextStatus] ?? ''
      currentBody.actualDate = {
        startDate:
          nextStatus === 'TODO' ? null : currentBody.scheduledDate?.startDate,
        endDate:
          nextStatus === 'DONE' ? currentBody.scheduledDate?.endDate : null,
      }
      applyAuditFieldsOnUpdate(currentBody, updatedBy)
    })
}

export const demoWbsItemTypeDtoByType: {
  [type: string]: typeof demoWbsItemTypeWorkgroup
} = {
  WORKGROUP: demoWbsItemTypeWorkgroup,
  PROCESS: demoWbsItemTypeProcess,
  DELIVERABLE_LIST: demoWbsItemTypeDeliverableList,
  DELIVERABLE: demoWbsItemTypeDeliverable,
  TASK: demoWbsItemType,
}

// ticketTypeごとのDELIVERABLE(チケット管理表)/TASK(チケット)向けtypeDto。
// demoTicketTypesはチケット管理表・チケット両方をフラットに含む配列なので、
// rootTypeで絞り込んで引く。
const demoTicketTypeDtoByRootTypeAndCode: {
  [rootType: string]: { [code: string]: (typeof demoTicketTypes)[number] }
} = {
  DELIVERABLE: Object.fromEntries(
    demoTicketTypes
      .filter(t => t.rootType === 'DELIVERABLE')
      .map(t => [t.code.replace('_LIST', ''), t])
  ),
  TASK: Object.fromEntries(
    demoTicketTypes.filter(t => t.rootType === 'TASK').map(t => [t.code, t])
  ),
}

// addedItem.type（WORKGROUP/PROCESS/DELIVERABLE/TASK）とaddedItem.ticketType
// （チケット管理表・チケット行のみ設定される）から適切なtypeDtoを解決する。
// ticketTypeが無い（通常のWBS行）場合はdemoWbsItemTypeDtoByTypeにフォール
// バックする。
//
// typeUuidが必要な理由: ワークグループを新規追加した行は、本体
// （createNewProjectPlanNewRow / ProjectPlanNew/projectPlanNew.ts）が
// `type.isWorkgroup() ? PROCESS : type.rootType`としてtypeを'PROCESS'に
// 潰して送ってくるため、typeだけでは工程と区別できず工程のtypeDto
// （名称「工程」・工程アイコン）になってしまう。一方wbsItemType自体は
// 潰されずtypeUuid（= wbsItem.wbsItemType.uuid, projectPlanNewData.ts）に
// ワークグループ型のuuidが残るので、そちらを先に見てルート行と同じ
// 「見た目はワークグループ・rootTypeはPROCESS」のtypeDtoへ寄せる。
export const resolveWbsItemTypeDto = (
  type: string,
  ticketType?: string,
  typeUuid?: string
): (typeof demoWbsItemTypes)[number] | (typeof demoTicketTypes)[number] => {
  if (typeUuid === demoWbsItemTypeWorkgroup.uuid) {
    return demoWbsItemTypeWorkgroupRoot
  }
  if (ticketType) {
    const dto = demoTicketTypeDtoByRootTypeAndCode[type]?.[ticketType]
    if (dto) return dto
  }
  return demoWbsItemTypeDtoByType[type]
}

// WbsItemTypeVO.canBeChildOf（src/domain/value-object/WbsItemTypeVO）をベースに、
// 実バックエンドと同じ親子の組み合わせ（例: DELIVERABLEの子はTASKのみで、
// 他のDELIVERABLEは不可）をモックでも拒否する。デモモードにはチケットの概念が
// ないため isTicket() 分岐は省略し、TASKは全て通常タスク（非チケット）として扱う。
// PROCESSだけは実物と異なり例外を追加している: 実物ではPROCESSの親は
// WORKGROUPだが、デモのルート行はワークグループ見た目のPROCESSであり
// （demoWbsItemTypeWorkgroupRoot参照）ツリー上にWORKGROUPが存在しないため、
// `parentType === 'PROJECT'`も許可してルート直下に配置できるようにしている
// （'PROJECT'はこのモック内だけの仮想親タイプで、ルートノード用に用いる）。
export const canBeChildOf = (
  childType: string,
  parentType: string
): boolean => {
  switch (childType) {
    case 'PROCESS':
      return (
        parentType === 'PROJECT' ||
        parentType === 'WORKGROUP' ||
        parentType === 'PROCESS'
      )
    case 'DELIVERABLE_LIST':
      return (
        parentType === 'WORKGROUP' ||
        parentType === 'PROCESS' ||
        parentType === 'DELIVERABLE_LIST'
      )
    case 'DELIVERABLE':
      return (
        parentType === 'WORKGROUP' ||
        parentType === 'PROCESS' ||
        parentType === 'DELIVERABLE_LIST'
      )
    case 'TASK':
      return parentType === 'DELIVERABLE' || parentType === 'TASK'
    default:
      return false
  }
}

// 指定uuidのノードを現在保持している配列（あるノードの`children`）を、
// ルートからスケルトンツリー全体を探索して見つける。
export const findParentChildrenArray = (
  targetUuid: string,
  node: any = demoWbsItemSkeletonRoot
): any[] | null => {
  const children = node.children as any[]
  if (children.some(child => child.uuid === targetUuid)) return children
  for (const child of children) {
    const found = findParentChildrenArray(targetUuid, child)
    if (found) return found
  }
  return null
}

// tickets/delta/batch・POST /ticketsで作られたノードはuuid（行uuid）と
// wbsItemUuidが別値のため、deleted項目がwbsItemUuidで渡ってくる呼び出し元
// （tickets/delta/batch・ticket_lists/delta/batchのdeletedループ）では
// 直接キー一致せず、削除がツリーに反映されないまま残ってしまう。
// demoWbsItemSkeletonNodesByUuidに無いuuidはwbsItemUuidとみなし、
// findSkeletonNodeByWbsItemUuidで行uuidへ変換してから探索する。
export const removeNodeFromTree = (uuid: string): void => {
  const targetUuid = demoWbsItemSkeletonNodesByUuid[uuid]
    ? uuid
    : (findSkeletonNodeByWbsItemUuid(uuid)?.uuid ?? uuid)
  const siblings = findParentChildrenArray(targetUuid)
  if (!siblings) return
  const index = siblings.findIndex(child => child.uuid === targetUuid)
  if (index !== -1) siblings.splice(index, 1)
}

// demoWbsItemSkeletonNodesByUuidは行（スケルトンノード）uuidでキーイング
// されているが、ticketListUuid（tickets/listのクエリパラメータやticket_lists
// /tickets delta/batchのparentUuid）はwbsItem uuid（demoWbsItemBodiesの
// キー）。ProjectPlanNew経由で新規作成された行は行uuidとwbsItem uuidが別値
// （createNewProjectPlanNewRow参照）のため、直接キーアクセスすると見つから
// ない。wbsItemUuidで逆引きする。
export const findSkeletonNodeByWbsItemUuid = (wbsItemUuid: string) =>
  Object.values(demoWbsItemSkeletonNodesByUuid).find(
    (node: any) => node.wbsItemUuid === wbsItemUuid
  )

// plans/body は projectPlanUuid（行uuid）だけでなく wbsItemCode でも引かれる
// （fetchProjectPlanBodyByCode。単票を閉じた後やWBSアイテム更新後の行再取得が
// こちら）。code は demoWbsItemBodies 側にしか無いため、code から
// wbsItemUuid、さらに行uuidへと辿れるようにする。cumulation は行uuid基準で
// 集計するため、行uuidまで解決できないと別ノードの集計値を掴んでしまう。
export const findSkeletonNodeByWbsItemCode = (wbsItemCode: string) => {
  const entry = Object.entries(demoWbsItemBodies).find(
    ([, body]: [string, any]) => body?.code === wbsItemCode
  )
  if (!entry) return undefined
  return findSkeletonNodeByWbsItemUuid(entry[0])
}

// `node`をuuid `prevSiblingUuid`の兄弟の直後に挿入する。前の兄弟がない場合は
// `parentUuid`のchildrenの先頭に挿入する。
export const insertNodeIntoTree = (
  node: any,
  parentUuid: string | undefined,
  prevSiblingUuid: string | undefined
): void => {
  // parentUuidはplans/delta/batchからは行（スケルトンノード）uuid、
  // tickets/delta/batch・ticket_lists/delta/batchからはwbsItem uuid
  // （ticketListUuid）で渡されてくる。直接キーアクセスだとチケット追加時
  // に親が見つからずルート直下へ挿入されてしまうため、行uuidで見つからな
  // ければwbsItem uuidとしても解決を試みる。
  const parentNode = parentUuid
    ? (demoWbsItemSkeletonNodesByUuid[parentUuid] ??
      findSkeletonNodeByWbsItemUuid(parentUuid) ??
      (parentUuid === demoWbsItemSkeletonRoot.uuid
        ? demoWbsItemSkeletonRoot
        : null))
    : demoWbsItemSkeletonRoot
  // parentUuidが指定されているにも関わらず解決できない場合、ルート直下への
  // フォールバック挿入は「移動先を無視して別の場所に置く」という誤動作に
  // なり気づきにくいため、ここで明示的に失敗させる。
  if (parentUuid && !parentNode) {
    throw new Error(`insertNodeIntoTree: unresolved parentUuid "${parentUuid}"`)
  }
  const children: any[] = parentNode.children
  // 兄弟探索も同じ理由（行uuid/wbsItem uuidの空間違い）でuuid一致だけでは
  // 足りないため、wbsItemUuid一致にもフォールバックする。
  const insertAt = prevSiblingUuid
    ? children.findIndex(
        child =>
          child.uuid === prevSiblingUuid ||
          child.wbsItemUuid === prevSiblingUuid
      ) + 1
    : 0
  children.splice(insertAt, 0, node)
}

// ticket_lists/basicハンドラ向け: TicketList.getByUuidInternalは
// TicketListDetail（wbsItemネスト形状）ではなくTicketListBasic
// （lib/functions/ticketList.ts、uuid/code/wbsItemUuid/ticketType/
// projectUuid/displayNameがトップレベル）を期待する。createNewTicketRow
// （Tickets/dataManager/index.ts）がこのトップレベルのcode/displayName等を
// 直接読むため、TicketListDetailをそのまま返すとundefinedになってしまう。
export const buildDemoTicketListBasic = (uuid: string) => {
  const body = demoWbsItemBodies[uuid]
  if (!body) return undefined
  return {
    uuid,
    code: body.code,
    wbsItemUuid: uuid,
    ticketType: body.ticketType,
    projectUuid: body.projectUuid,
    displayName: body.displayName,
  }
}

// ticket_lists/ticketsハンドラ向け: demoWbsItemBodiesのticketType(_LIST無し)
// を持つエントリ（チケットリスト自身）をTicketListDetail形状に変換する。
// projectPlanCumulation/ticketCumulationは表示用の集計値なので、デモでは
// 全て0埋めでよい。
export const buildDemoTicketListDetail = (uuid: string) => {
  const body = demoWbsItemBodies[uuid]
  if (!body) return undefined
  return {
    uuid,
    lockVersion: body.lockVersion,
    wbsItem: body,
    ticketType: body.ticketType,
    projectPlanCumulation: {},
    ticketCumulation: {
      ticketListUuid: uuid,
      countTicket: 0,
      countTicketDone: 0,
      sumEstimatedHour: 0,
      sumActualHour: 0,
    },
    createdAt: 0,
    createdBy: demoUser,
    updatedAt: 0,
    updatedBy: demoUser,
    children: [],
  }
}

// plans/skeleton（fetchBodyUuid付き）・plans/bodyハンドラ向け:
// ProjectPlanRowBody(src/view/pages/ProjectPlanNew/projectPlanNew.ts)が
// 期待するProjectPlanBody形状（{wbsItem, ticketCumulation?}）を組み立てる。
// ticketCumulation.ticketListUuidはgetTargetRowData
// （ProjectPlanNew/hooks/actionPopper.ts）がチケットリスト行自身を右クリック
// して「子にチケット追加」した際の親チケットリストuuid解決に使う
// （その行はwbsItem.ticketListUuidを持たないチケットリスト自身なので）。
export const buildDemoProjectPlanBody = (wbsItemUuid: string) => {
  const body = demoWbsItemBodies[wbsItemUuid]
  if (!body) return undefined
  const isTicketList = body.type === 'DELIVERABLE' && !!body.ticketType
  return {
    wbsItem: body,
    commentSummary: demoCommentSummary(wbsItemUuid),
    ...(isTicketList
      ? { ticketCumulation: { ticketListUuid: wbsItemUuid } }
      : {}),
  }
}

// ticket_lists/listハンドラ向け: demoWbsItemBodies全体を都度スキャンして
// チケットリストを集める。DEMO_TICKET_LIST_UUIDSだけを見る実装だと、
// ユーザーが画面から新規作成したチケット管理表（ランダムなwbsItemUuidを持つ）
// が一生反映されないため、都度re-scanが必須。
// チケット管理表・チケット本体はどちらもticketTypeに同じ値（例: 'ISSUE'）を
// 持つ（createNewProjectPlanNewRowがtype.code.replace('_LIST', '')で導出する
// ため、管理表側も'_LIST'サフィックスは付かない）。両者はwbsItem.type
// （管理表はDELIVERABLE、チケット本体はTASK）で区別する。
export const findAllDemoTicketLists = () =>
  Object.keys(demoWbsItemBodies)
    .filter(
      uuid =>
        demoWbsItemBodies[uuid].type === 'DELIVERABLE' &&
        !!demoWbsItemBodies[uuid].ticketType
    )
    .map(buildDemoTicketListDetail)
    .filter((v): v is NonNullable<typeof v> => !!v)

// tickets/list・tickets/detailハンドラ向け: demoWbsItemBodiesのticketListUuid
// を持つエントリ（チケット自身）をTicketDetail形状に変換する。
export const buildDemoTicketDetail = (uuid: string) => {
  // tickets/detail (useTicketsData.refreshSingleRow) queries by the ticket
  // ROW uuid, which differs from the wbsItem uuid demoWbsItemBodies is keyed
  // by (see addTicket/POST /tickets); fall back to resolving it through the
  // skeleton node before giving up.
  const body =
    demoWbsItemBodies[uuid] ??
    demoWbsItemBodies[demoWbsItemSkeletonNodesByUuid[uuid]?.wbsItemUuid]
  // ticketListUuid can legitimately be '' (ticket list left unselected in
  // the single-sheet dialog); only a missing body means "not saved yet".
  if (!body) return undefined
  const ticketListBody = demoWbsItemBodies[body.ticketListUuid]
  return {
    uuid: body.uuid,
    lockVersion: body.lockVersion,
    wbsItem: body,
    revision: 'demo-revision',
    // fromApiResponseToEntity (domain/entity/ticket) reads
    // src.ticketList.uuid/.displayName/.ticketType with no optional
    // chaining, so an unselected ticket list (ticketListBody undefined)
    // needs a stub here instead of undefined.
    ticketList: ticketListBody ?? {
      uuid: '',
      displayName: '',
      ticketType: '',
    },
    parentWbsItem: {
      uuid: body.ticketListUuid,
      code: ticketListBody?.code,
      projectUuid: demoProject.uuid,
      projectCode: demoProject.code,
      type: ticketListBody?.type,
      typeDto: ticketListBody?.typeDto,
      displayName: ticketListBody?.displayName,
      ticketType: '',
      ticketUuid: '',
      ticketListUuid: '',
    },
    cumulation: {},
    commentSummary: { commentCount: 0 },
    ticketType: body.ticketType,
    children: [],
  }
}

// 表示順はdemoWbsItemBodiesのキー挿入順（＝作成順）ではなく、
// insertNodeIntoTreeが管理するスケルトンツリーの並び順（ドラッグ&ドロップや
// prevSiblingUuid指定を反映した順）に従う必要がある。そうしないと、追加した
// チケットが本来の挿入位置ではなくリロード後に先頭/末尾へ移動して見えてしまう。
export const findDemoTicketsByTicketListUuid = (ticketListUuid: string) => {
  const ticketListNode = findSkeletonNodeByWbsItemUuid(ticketListUuid)
  const orderedUuids: string[] =
    ticketListNode?.children.map((child: any) => child.wbsItemUuid) ?? []
  return orderedUuids
    .filter(uuid => demoWbsItemBodies[uuid]?.ticketListUuid === ticketListUuid)
    .map(buildDemoTicketDetail)
    .filter((v): v is NonNullable<typeof v> => !!v)
}

// カンバン（kanban/status）の検索条件rootUuidは「このWBSアイテム配下だけに
// 絞り込む」ためのもの。指定wbsItemUuid配下（自身は含まない）にある全ての
// TASKのwbsItemUuidを再帰的に集めて返す。
export const collectDescendantTaskWbsItemUuids = (
  wbsItemUuid: string
): Set<string> => {
  const result = new Set<string>()
  const rootNode = demoWbsItemSkeletonNodesByUuid[wbsItemUuid]
  const visit = (node: any) => {
    ;(node.children ?? []).forEach((child: any) => {
      const childBody = demoWbsItemBodies[child.wbsItemUuid]
      if (childBody?.type === 'TASK') {
        result.add(child.wbsItemUuid)
      }
      visit(child)
    })
  }
  if (rootNode) visit(rootNode)
  return result
}

// プロジェクト計画画面の「進捗状況」列グループ（予定進捗率/進捗率/総数/
// 予定完了/完了/先行/遅延/残）とEVM列グループ（CPI/SPI/ETC/EAC/CV/SV/VAC）は
// Progress.of(cumulation, context)（src/view/pages/ProjectPlanNew/gridOptions/
// cumulation.ts）がProjectPlanCumulationのsum*/count*フィールドから算出する。
// 本体のCumulationProjectorは編集差分を積み上げる設計でモックの静的
// ツリーには使いにくいため、ここではデモツリーの子孫DELIVERABLE/TASKを
// 都度再帰走査して同じ形のオブジェクトを組み立てる簡易版を用意する
// （実APIやDBへは一切接続しない、モック内で完結した計算ロジック）。
// ProjectPlanCumulation本体の全フィールドをデフォルト値0で持たせておくことで、
// ProjectPlanCumulation.sum()（レポートツールバーの集計）が未加算フィールドを
// undefinedとして加算しNaNに汚染することを防ぐ。
export type DemoProjectPlanCumulation = ProjectPlanCumulation
const createEmptyDemoCumulation = (): DemoProjectPlanCumulation =>
  new ProjectPlanCumulation()

// addDemoWbsItemProgressが参照するdemoWbsItemBodies要素の最小形。
// demoWbsItemBodies自体は`{ [uuid: string]: any }`だが、ここで使う
// プロパティだけを明示しanyの伝播を止める。
type DemoWbsItemProgressSource = {
  type: string
  status: DemoWbsItemStatus
  estimatedHour?: number
  actualHour?: number
  scheduledDate?: { endDate?: string }
}

// 1件のDELIVERABLE/TASK bodyの増分をcumulationへ加算する。
// 判定基準はProjectPlanNew/gridOptions/cumulation.tsのProgressDetail
// （total/scheduledToBeCompleted/completed/endPreceding/endDelayed）に
// 合わせている: 予定終了日が今日以前なら「予定完了」対象、DONEなら
// 「完了」、完了済みで予定終了日が未来なら「先行」、未完了で予定終了日が
// 過去なら「遅延」。
const addDemoWbsItemProgress = (
  cumulation: DemoProjectPlanCumulation,
  body: DemoWbsItemProgressSource,
  today: string
): void => {
  const isDeliverable = body.type === 'DELIVERABLE'
  const isTask = body.type === 'TASK'
  if (!isDeliverable && !isTask) return

  const hour = body.estimatedHour ?? 0
  const status = body.status
  const scheduledEndDate = body.scheduledDate?.endDate

  if (isDeliverable) {
    cumulation.countDeliverable += 1
    cumulation.sumDeliverableEstimatedHour += hour
  } else {
    cumulation.countTask += 1
    cumulation.sumTaskEstimatedHour += hour
    cumulation.sumActualHour += body.actualHour ?? 0
  }

  if (status === WbsItemStatus.DISCARD) {
    if (isDeliverable) {
      cumulation.sumDeliverableEstimatedHourDiscard += hour
      cumulation.countStatusDeliverableDiscard += 1
    } else {
      cumulation.sumTaskEstimatedHourDiscard += hour
      cumulation.countStatusTaskDiscard += 1
    }
    return
  }

  const isScheduledToBeCompleted =
    !!scheduledEndDate && scheduledEndDate <= today
  const isDone = status === WbsItemStatus.DONE
  const isPreceding = isDone && !!scheduledEndDate && today < scheduledEndDate
  const isDelayed = !isDone && !!scheduledEndDate && scheduledEndDate < today

  if (isDeliverable) {
    if (isScheduledToBeCompleted) {
      cumulation.sumDeliverableToBeCompleted += hour
      cumulation.countDeliverableToBeCompleted += 1
    }
    if (isDone) {
      cumulation.sumDeliverableEstimatedHourDone += hour
      cumulation.countStatusDeliverableDone += 1
    }
    if (isPreceding) {
      cumulation.sumDeliverableEndPreceding += hour
      cumulation.countDeliverableEndPreceding += 1
    }
    if (isDelayed) {
      cumulation.sumDeliverableEndDelayed += hour
      cumulation.countDeliverableEndDelayed += 1
    }
  } else {
    if (isScheduledToBeCompleted) {
      cumulation.sumTaskToBeCompleted += hour
      cumulation.countTaskToBeCompleted += 1
    }
    if (isDone) {
      cumulation.sumTaskEstimatedHourDone += hour
      cumulation.countStatusTaskDone += 1
    }
    if (isPreceding) {
      cumulation.sumTaskEndPreceding += hour
      cumulation.countTaskEndPreceding += 1
    }
    if (isDelayed) {
      cumulation.sumTaskEndDelayed += hour
      cumulation.countTaskEndDelayed += 1
    }
  }
}

// addWbsItemが生成するスケルトンノードの形（再帰的なchildrenを持つ）。
type DemoWbsItemSkeletonNode = {
  uuid: string
  wbsItemUuid: string
  children: DemoWbsItemSkeletonNode[]
}

// nodeUuid（スケルトンツリーのノードuuid）配下の子孫DELIVERABLE/TASKを
// 再帰的に集計し、進捗状況列グループが読むProjectPlanCumulation互換の
// オブジェクトを返す。葉ノード自身（DELIVERABLE/TASK）はcumulationを
// 持たない実物の挙動に合わせ、それ以外のノード（WORKGROUP/PROCESS等）に
// のみ意味のある値を返す。
// 単一行のcumulationだけが必要な呼び出し向け（plans/body等の絞り込み
// 取得）。全行分が必要な場合はcomputeAllDemoCumulationsを使うこと
// （こちらをノード数分ループさせるとツリー全体を毎回再走査してしまい、
// ノード数の2乗に比例して遅くなる）。
export const computeDemoCumulation = (
  nodeUuid: string
): DemoProjectPlanCumulation => {
  const cumulation: DemoProjectPlanCumulation = createEmptyDemoCumulation()
  const rootNode: DemoWbsItemSkeletonNode | undefined =
    demoWbsItemSkeletonNodesByUuid[nodeUuid]
  if (!rootNode) return cumulation

  const today = dayOffsetToDate(0)
  const visit = (node: DemoWbsItemSkeletonNode) => {
    ;(node.children ?? []).forEach(child => {
      const childBody = demoWbsItemBodies[child.wbsItemUuid]
      if (childBody) addDemoWbsItemProgress(cumulation, childBody, today)
      visit(child)
    })
  }
  visit(rootNode)
  return cumulation
}

// cumulationの数値フィールドをsrcからdestへ加算する。ProjectPlanCumulation
// はsum*/count*の数値フィールドのみが集計対象で、projectPlanUuid等の
// 非数値フィールドは対象外のため、typeof値で判定して自動判別する。
const addCumulationInto = (
  dest: DemoProjectPlanCumulation,
  src: DemoProjectPlanCumulation
): void => {
  for (const key of Object.keys(dest) as (keyof DemoProjectPlanCumulation)[]) {
    const value = src[key]
    if (typeof value === 'number') {
      ;(dest[key] as number) += value
    }
  }
}

// plans/bodyが全行分のcumulationを返す際に使う一括計算版。ノードごとに
// computeDemoCumulationを呼ぶとツリー全体をノード数分再走査してしまい
// ノード数の2乗に比例して遅くなるため、木を1回だけ後行順（子→親）に
// 走査し、各ノードのcumulationを子のcumulationとその直下のDELIVERABLE/TASK
// から積み上げて求める（全体でノード数に比例する計算量に収まる）。
export const computeAllDemoCumulations = (): {
  [rowUuid: string]: DemoProjectPlanCumulation
} => {
  const cumulationsByRowUuid: { [rowUuid: string]: DemoProjectPlanCumulation } =
    {}
  const today = dayOffsetToDate(0)

  const visit = (node: DemoWbsItemSkeletonNode): DemoProjectPlanCumulation => {
    const cumulation = createEmptyDemoCumulation()
    ;(node.children ?? []).forEach(child => {
      const childBody = demoWbsItemBodies[child.wbsItemUuid]
      if (childBody) addDemoWbsItemProgress(cumulation, childBody, today)
      addCumulationInto(cumulation, visit(child))
    })
    cumulationsByRowUuid[node.uuid] = cumulation
    return cumulation
  }
  visit(demoWbsItemSkeletonRoot)

  return cumulationsByRowUuid
}

const isWithinDateRange = (
  date: string | undefined,
  from: string | null,
  to: string | null
): boolean => {
  if (!date) return false
  if (from && date < from) return false
  if (to && date > to) return false
  return true
}

// ProjectPlanNew の列フィルター（ServerSideXxxFilter群）が投げる
// GET /api/v1/projects/plans/filter_by_wbs_item 用の条件パーサー。
// toUrlQuery（src/lib/commons/api.ts）はネストしたフィルターオブジェクトを
// `key.field=value`のドット区切りへ展開するため、URLSearchParamsからは
// そのプレフィックス経由で個々の条件を読み戻す。
const getTextFilterParam = (
  searchParams: URLSearchParams,
  key: string
): { operator: string; value: string } | undefined => {
  const operator = searchParams.get(`${key}.operator`)
  if (!operator) return undefined
  return { operator, value: searchParams.get(`${key}.value`) ?? '' }
}

const getNumberFilterParam = (
  searchParams: URLSearchParams,
  key: string
): { operator: string; value: number | undefined } | undefined => {
  const operator = searchParams.get(`${key}.operator`)
  if (!operator) return undefined
  const rawValue = searchParams.get(`${key}.value`)
  return { operator, value: rawValue === null ? undefined : Number(rawValue) }
}

const getDateFilterParam = (
  searchParams: URLSearchParams,
  key: string
): { operator: string; value: string } | undefined => {
  const operator = searchParams.get(`${key}.operator`)
  if (!operator) return undefined
  return { operator, value: searchParams.get(`${key}.value`) ?? '' }
}

const getBoolFilterParam = (
  searchParams: URLSearchParams,
  key: string
): { operator: string } | undefined => {
  const operator = searchParams.get(`${key}.operator`)
  return operator ? { operator } : undefined
}

const getSelectFilterParam = (
  searchParams: URLSearchParams,
  key: string
): { values: string[]; includeBlank: boolean } | undefined => {
  const includeBlankRaw = searchParams.get(`${key}.includeBlank`)
  if (includeBlankRaw === null) return undefined
  return {
    values:
      searchParams
        .get(`${key}.values`)
        ?.split(',')
        .filter(v => v) ?? [],
    includeBlank: includeBlankRaw === 'true',
  }
}

const matchesTextFilter = (
  actual: string | undefined,
  condition?: { operator: string; value: string }
): boolean => {
  if (!condition) return true
  const target = actual ?? ''
  switch (condition.operator) {
    case 'BLANK':
      return target === ''
    case 'NOT_BLANK':
      return target !== ''
    case 'EQUALS':
      return target === condition.value
    case 'NOT_EQUALS':
      return target !== condition.value
    case 'STARTS_WITH':
      return target.startsWith(condition.value)
    case 'ENDS_WITH':
      return target.endsWith(condition.value)
    case 'NOT_CONTAINS':
      return !target.includes(condition.value)
    case 'CONTAINS':
    default:
      return target.includes(condition.value)
  }
}

const matchesNumberFilter = (
  actual: number | undefined,
  condition?: { operator: string; value: number | undefined }
): boolean => {
  if (!condition) return true
  if (condition.operator === 'BLANK') return actual === undefined
  if (condition.operator === 'NOT_BLANK') return actual !== undefined
  if (actual === undefined) return false
  const value = condition.value
  if (value === undefined) return true
  switch (condition.operator) {
    case 'NOT_EQUALS':
      return actual !== value
    case 'LESS_THAN':
      return actual < value
    case 'LESS_THAN_OR_EQUALS':
      return actual <= value
    case 'GREATER_THAN':
      return actual > value
    case 'GREATER_THAN_OR_EQUALS':
      return actual >= value
    case 'EQUALS':
    default:
      return actual === value
  }
}

const matchesDateFilter = (
  actual: string | null | undefined,
  condition?: { operator: string; value: string }
): boolean => {
  if (!condition) return true
  if (condition.operator === 'BLANK') return !actual
  if (!actual) return false
  switch (condition.operator) {
    case 'AFTER':
      return actual > condition.value
    case 'BEFORE':
      return actual < condition.value
    case 'EQUALS':
    default:
      return actual === condition.value
  }
}

const matchesBoolFilter = (
  actual: boolean,
  condition?: { operator: string }
): boolean => {
  if (!condition) return true
  switch (condition.operator) {
    case 'NOT_EXIST':
    case 'ALL_DESELECTED':
      return !actual
    case 'EXIST':
    default:
      return actual
  }
}

const matchesSelectFilter = (
  actual: string | undefined,
  condition?: { values: string[]; includeBlank: boolean }
): boolean => {
  if (!condition) return true
  if (!actual) return condition.includeBlank
  return condition.values.includes(actual)
}

// スプリントはbody.sprints（配列）で保持するため、選択された値のいずれかを
// 含めばマッチとする。未割り当ての行はincludeBlank指定時のみ残す。
const matchesSprintFilter = (
  actual: { uuid: string }[] | undefined,
  condition?: { values: string[]; includeBlank: boolean }
): boolean => {
  if (!condition) return true
  if (!actual || actual.length === 0) return condition.includeBlank
  return actual.some(sprint => condition.values.includes(sprint.uuid))
}

// filter_by_wbs_item はマッチした行のwbsItem uuid配列を返すだけの単純なAPIで、
// フロント（ServerSideXxxFilter群）はそれをSetにしてAgGridのdoesFilterPassで
// 突き合わせるだけ。デモデータが持たない条件（tags/attachment/
// comment/createdBy等）は対象外とし、その条件が来た場合は該当なし扱いにする。
// スプリントはdemoSprints追加後、body.sprintsに割り当てを保持するため対応済み。
export const filterDemoWbsItemBodiesByProjectPlanFilter = (
  searchParams: URLSearchParams
): string[] => {
  const type = getSelectFilterParam(searchParams, 'type')
  const code = getTextFilterParam(searchParams, 'code')
  const displayName = getTextFilterParam(searchParams, 'displayName')
  const description = getTextFilterParam(searchParams, 'description')
  const estimatedHour = getNumberFilterParam(searchParams, 'estimatedHour')
  const estimatedStoryPoint = getNumberFilterParam(
    searchParams,
    'estimatedStoryPoint'
  )
  const estimatedAmount = getNumberFilterParam(searchParams, 'estimatedAmount')
  const actualAmount = getNumberFilterParam(searchParams, 'actualAmount')
  const actualHour = getNumberFilterParam(searchParams, 'actualHour')
  const scheduledStartDate = getDateFilterParam(
    searchParams,
    'scheduledStartDate'
  )
  const scheduledEndDate = getDateFilterParam(searchParams, 'scheduledEndDate')
  const actualStartDate = getDateFilterParam(searchParams, 'actualStartDate')
  const actualEndDate = getDateFilterParam(searchParams, 'actualEndDate')
  const status = getSelectFilterParam(searchParams, 'status')
  const substatus = getSelectFilterParam(searchParams, 'substatus')
  const priority = getSelectFilterParam(searchParams, 'priority')
  const difficulty = getSelectFilterParam(searchParams, 'difficulty')
  const team = getSelectFilterParam(searchParams, 'team')
  const sprint = getSelectFilterParam(searchParams, 'sprint')
  const accountable = getSelectFilterParam(searchParams, 'accountable')
  const responsible = getSelectFilterParam(searchParams, 'responsible')
  const assignee = getSelectFilterParam(searchParams, 'assignee')
  const watcher = getSelectFilterParam(searchParams, 'watcher')
  const attachment = getBoolFilterParam(searchParams, 'attachment')
  const commentExist = getBoolFilterParam(searchParams, 'commentExist')
  // デモデータには添付・コメントが存在しないため、指定された場合は
  // 「EXIST」以外の演算子のみ該当しうる。
  const hasNoAttachmentOrComment = (condition?: { operator: string }) =>
    matchesBoolFilter(false, condition)

  return Object.entries(demoWbsItemBodies)
    .filter(([, body]) => {
      if (!matchesSelectFilter(body.type, type)) return false
      if (!matchesTextFilter(body.code, code)) return false
      if (!matchesTextFilter(body.displayName, displayName)) return false
      if (!matchesTextFilter(body.description, description)) return false
      if (!matchesNumberFilter(body.estimatedHour, estimatedHour)) return false
      if (!matchesNumberFilter(body.estimatedStoryPoint, estimatedStoryPoint)) {
        return false
      }
      if (!matchesNumberFilter(body.estimatedAmount, estimatedAmount)) {
        return false
      }
      if (!matchesNumberFilter(body.actualAmount, actualAmount)) return false
      if (!matchesNumberFilter(body.actualHour, actualHour)) return false
      if (
        !matchesDateFilter(body.scheduledDate?.startDate, scheduledStartDate)
      ) {
        return false
      }
      if (!matchesDateFilter(body.scheduledDate?.endDate, scheduledEndDate)) {
        return false
      }
      if (!matchesDateFilter(body.actualDate?.startDate, actualStartDate)) {
        return false
      }
      if (!matchesDateFilter(body.actualDate?.endDate, actualEndDate)) {
        return false
      }
      if (!matchesSelectFilter(body.status, status)) return false
      if (!matchesSelectFilter(body.substatus, substatus)) return false
      if (!matchesSelectFilter(body.priority, priority)) return false
      if (!matchesSelectFilter(body.difficulty, difficulty)) return false
      if (!matchesSelectFilter(body.team?.uuid, team)) return false
      // スプリントは1WBSアイテムに複数割り当てられる（sprints-multi-select）
      // ため、単一値のmatchesSelectFilterではなくいずれかが一致すればよい。
      if (!matchesSprintFilter(body.sprints, sprint)) return false
      // accountable/responsible/assignee/watchersはproject member（uuidが
      // ユーザーとは別）で保存されるが、フィルター選択肢は
      // fetchMemberOptions（useWbsItemSelectColumnOptions.ts）経由でuser側
      // のuuidを渡してくる。突き合わせにはmember.userUuidを使う必要がある。
      if (!matchesSelectFilter(body.accountable?.userUuid, accountable)) {
        return false
      }
      if (!matchesSelectFilter(body.responsible?.userUuid, responsible)) {
        return false
      }
      if (!matchesSelectFilter(body.assignee?.userUuid, assignee)) {
        return false
      }
      if (
        watcher &&
        !watcher.values.some(v =>
          (body.watchers ?? []).some((w: any) => w.userUuid === v)
        )
      ) {
        return false
      }
      if (attachment && !hasNoAttachmentOrComment(attachment)) return false
      if (commentExist && !hasNoAttachmentOrComment(commentExist)) return false
      return true
    })
    .map(([uuid]) => uuid)
}

// kanban/status・kanban/status/count共通のフィルタ。Kanban画面の検索条件
// (WbsItemSearchConditionApiRequest) のうち、デモデータで意味のある絞り込み
// （ルートWBS配下、ステータス、優先度、コード／名称の部分一致、担当者系、
// 予定開始日・予定終了日の範囲）を対象にする。タグ・チームなどデモボディが
// 持たない/使わない条件は対応しない。msw（ESM専用依存を持つ）に依存しない
// よう、handlers.tsではなくここに置く（jestでのユニットテストを可能にするため）。
export const filterDemoTaskEntriesBySearchCondition = (
  searchParams: URLSearchParams
): [string, any][] => {
  const rootUuid = searchParams.get('rootUuid')
  const statusFilter = searchParams
    .get('status')
    ?.split(',')
    .filter(v => v)
  const priorityFilter = searchParams
    .get('priority')
    ?.split(',')
    .filter(v => v)
  const code = searchParams.get('code')
  const displayName = searchParams.get('displayName')
  const accountableUuid = searchParams.get('accountable.uuid')
  const responsibleUuid = searchParams.get('responsible.uuid')
  const assigneeUuid = searchParams.get('assignee.uuid')
  const teamUuid = searchParams.get('team.uuid')
  const scheduledStartDateFrom = searchParams.get('scheduledStartDate.from')
  const scheduledStartDateTo = searchParams.get('scheduledStartDate.to')
  const scheduledEndDateFrom = searchParams.get('scheduledEndDate.from')
  const scheduledEndDateTo = searchParams.get('scheduledEndDate.to')

  const descendantTaskUuids = rootUuid
    ? collectDescendantTaskWbsItemUuids(rootUuid)
    : undefined

  return Object.entries(demoWbsItemBodies).filter(([uuid, body]) => {
    if (body.type !== 'TASK') return false
    if (descendantTaskUuids && !descendantTaskUuids.has(uuid)) return false
    if (statusFilter?.length && !statusFilter.includes(body.status)) {
      return false
    }
    if (priorityFilter?.length && !priorityFilter.includes(body.priority)) {
      return false
    }
    if (code && !body.code?.includes(code)) return false
    if (displayName && !body.displayName?.includes(displayName)) return false
    if (accountableUuid && body.accountable?.uuid !== accountableUuid) {
      return false
    }
    if (responsibleUuid && body.responsible?.uuid !== responsibleUuid) {
      return false
    }
    if (assigneeUuid && body.assignee?.uuid !== assigneeUuid) return false
    if (teamUuid && body.team?.uuid !== teamUuid) return false
    if (
      (scheduledStartDateFrom || scheduledStartDateTo) &&
      !isWithinDateRange(
        body.scheduledDate?.startDate,
        scheduledStartDateFrom,
        scheduledStartDateTo
      )
    ) {
      return false
    }
    if (
      (scheduledEndDateFrom || scheduledEndDateTo) &&
      !isWithinDateRange(
        body.scheduledDate?.endDate,
        scheduledEndDateFrom,
        scheduledEndDateTo
      )
    ) {
      return false
    }
    return true
  })
}

// スプリント割り当て（sprint_product_items/batch = DELIVERABLE、
// sprint_backlogs/batch = TASK）をデモボディへ反映する。
// グリッドはwbsItem.sprints（配列）を読み、複数選択OFF時に参照される
// wbsItem.sprintはprojectPlanNewDataがsprintsから復元するため、
// ここではsprintsを正としてsprintも同期しておく。
export const applyDemoSprintAssignment = ({
  wbsItemUuid,
  sprintUuid,
  assign,
}: {
  wbsItemUuid: string
  sprintUuid: string
  assign: boolean
}): void => {
  const body = demoWbsItemBodies[wbsItemUuid]
  if (!body) return
  const currentSprints: { uuid: string; num: number }[] = body.sprints ?? []
  const withoutTarget = currentSprints.filter(
    sprint => sprint.uuid !== sprintUuid
  )
  const sprint = demoSprintsByUuid[sprintUuid]
  // 削除時、および存在しないスプリントを指定された場合は追加しない
  const nextSprints =
    assign && sprint ? [...withoutTarget, sprint] : withoutTarget
  const sorted = [...nextSprints].sort((a, b) => a.num - b.num)
  body.sprints = sorted
  // 単一選択の表示・保存経路が読むsprintは最新（最大num）のもの
  body.sprint = sorted.length > 0 ? sorted[sorted.length - 1] : undefined
}
